import React, { useState, useEffect } from 'react';
import { formations, defaultFormations, formationPositions, formationTransitions } from '../../utils/formations';
import PlayerShirt from './PlayerShirt';
import { getTeamKitColour, WHITE_SHIRT_COLOUR } from '../../utils/teamSettings';
import PlayerCard from './PlayerCard';
import api from '../../services/apiService';    
import html2canvas from 'html2canvas';
import logo from '../../images/logo-long.svg';
import { format } from 'date-fns';
import LineupListView from './LineupListView';
import { authService } from '../../services/authService';

/**
 * Formation Effectiveness Calculations
 * ----------------------------------
 * 
 * The formation effectiveness is calculated based on:
 * 1. Player Stats
 *    - Forward: Average of shooting and pace
 *    - Midfield: Average of passing and dribbling
 *    - Defense: Average of defending and physical
 * 
 * 2. Position Suitability Multipliers
 *    Forward positions:
 *    - Natural forwards (FWD): 1.0x
 *    - Midfielders: 0.7x
 *    - Defenders: 0.4x
 *    - Goalkeepers: 0.3x
 * 
 *    Midfield positions:
 *    - Natural midfielders (MID): 1.0x
 *    - Forwards: 0.7x
 *    - Defenders: 0.7x
 *    - Goalkeepers: 0.4x
 * 
 *    Defense positions:
 *    - Natural defenders (DEF/GK): 1.0x/0.9x
 *    - Midfielders: 0.7x
 *    - Forwards: 0.4x
 * 
 * Example:
 * A forward with 80 shooting/pace in attack = 80 * 1.0 = 80 effectiveness
 * A defender with 80 shooting/pace in attack = 80 * 0.4 = 32 effectiveness
 * 
 * The final rating for each position (attack/midfield/defense) is the average
 * effectiveness of all players in that position.
 */

const TeamBuilder = ({ teams, isAdmin, match, playerDetails }) => {
    // Initialize states
    console.log("TeamBuilder initializing with match:", {
        id: match?.match_id,
        team_size: match?.team_size,
        max_players: match?.max_players,
        team_type: match?.team_type
    });
    
    // Add this function to handle the team size conversion
    const getTeamSizeFormat = (teamSizeFormat) => {
        // Add detailed debugging
        console.log(`Processing team size format: "${teamSizeFormat}"`);
        
        // If no team size format is provided, return null
        if (!teamSizeFormat) {
            console.log('No team size format provided');
            return null;
        }
        
        // Handle both formats: "11v11" and "11 v 11" by removing spaces
        const cleanFormat = teamSizeFormat.replace(/\s+/g, '').toLowerCase();
        console.log(`Cleaned format: "${cleanFormat}"`);
        
        // Extract the number before "v" to get players per side (including goalkeeper)
        const parts = cleanFormat.split('v');
        if (parts.length !== 2) {
            console.log('Invalid format - missing "v"');
            return null;
        }
        
        const playersPerSide = parseInt(parts[0]);
        
        if (isNaN(playersPerSide)) {
            console.log('Invalid format - not a number');
            return null;
        }
        
        // Check permanent_keeper setting in session storage
        const permanentKeeper = sessionStorage.getItem('permanent_keeper');
        console.log(`Permanent keeper setting: ${permanentKeeper}`);
        
        // If permanent_keeper is not set or is "0", we don't include a dedicated keeper
        // in this case, all players are outfield players
        if (permanentKeeper === null || permanentKeeper === "0") {
            console.log(`No permanent keeper needed: ${playersPerSide} outfield players per team`);
            return playersPerSide;
        }
        
        // If we have permanent_keeper = "1", subtract 1 for the goalkeeper to get outfield players per team
        const outfieldPlayers = playersPerSide - 1;
        console.log(`Team size: ${playersPerSide}v${playersPerSide} (${outfieldPlayers} outfield players per team with dedicated keeper)`);
        
        return outfieldPlayers;
    };
    
    // Get team type from sessionStorage first, before any other code that might use it
    const selectedTeamType = sessionStorage.getItem('selected_team_type');
    const needsTwoTeams = !['academy', 'squad'].includes(selectedTeamType);

    if (match?.team_size === '11v11' || (match?.team_size && match.team_size.replace(/\s+/g, '').toLowerCase() === '11v11')) {
        console.log("⚽ DETECTED 11v11 MATCH FORMAT - This should trigger specialized 11v11 handling");
    }
    
    // Effect to ensure activeTeam is always 'white' for academy/squad types
    React.useEffect(() => {
        if (!needsTwoTeams) {
            setActiveTeam('white');
        }
    }, [needsTwoTeams]);
    
    const [whiteFormation, setWhiteFormation] = React.useState(() => {
        // If we have a saved formation from the match, use it
        if (match?.white_formation) return match.white_formation;
        
        // For academy/squad matches with team_size defined, use appropriate formation
        if (match?.team_size && ['academy', 'squad'].includes(selectedTeamType)) {
            const outfieldPlayersPerTeam = getTeamSizeFormat(match.team_size);
            if (outfieldPlayersPerTeam && formations[outfieldPlayersPerTeam]) {
                // Use the first formation for this team size
                return formations[outfieldPlayersPerTeam][0];
            }
        }
        
        // Default to 4-a-side formation
        return '1-2-1';
    });
    
    const [colourFormation, setColourFormation] = React.useState(() => {
        // If we have a saved formation from the match, use it
        if (match?.colour_formation) return match.colour_formation;
        
        // For academy/squad matches with team_size defined, use appropriate formation
        if (match?.team_size && ['academy', 'squad'].includes(selectedTeamType)) {
            const outfieldPlayersPerTeam = getTeamSizeFormat(match.team_size);
            if (outfieldPlayersPerTeam && formations[outfieldPlayersPerTeam]) {
                // Use the first formation for this team size
                return formations[outfieldPlayersPerTeam][0];
            }
        }
        
        // Default to 4-a-side formation
        return '1-2-1';
    });
    const [activeTeam, setActiveTeam] = React.useState('white');  // 'white' or 'colour'
    const [isLoading, setIsLoading] = React.useState(true);
    const [hasLoadedPositions, setHasLoadedPositions] = React.useState(false);
    const [showLineupList, setShowLineupList] = React.useState(false);
    const [screenshotPreview, setScreenshotPreview] = React.useState(null);
    const [selectedPlayer, setSelectedPlayer] = React.useState(null);
    const [selectedPosition, setSelectedPosition] = React.useState(null);
    const [isMatchInPast, setIsMatchInPast] = React.useState(false);
    const [positionWarning, setPositionWarning] = React.useState(null);
    const [teamsPublished, setTeamsPublished] = React.useState(false); // Track if teams have been published

    // Default to 6-a-side if match details not loaded yet
    const maxPlayers = match?.max_players || 6;
    
    // Add debug state
    const [debugInfo, setDebugInfo] = React.useState(null);
    const [showDebugPanel, setShowDebugPanel] = React.useState(false); // Set to false by default
    const [dropouts, setDropouts] = React.useState([]);
    
    // Get team names from match object, fallback to defaults
    const teamWhiteName = match?.team_a || 'Whites';
    const teamColourName = match?.team_b || 'Colours';

    // Add isMatchFinished function
    const isMatchFinished = () => {
        if (!match) {
            console.log('isMatchFinished: No match data available');
            return false;
        }
        
        if (!match.match_date || !match.match_time) {
            console.log('isMatchFinished: Missing match_date or match_time');
            console.log('- match_date:', match.match_date);
            console.log('- match_time:', match.match_time);
            return false;
        }
        
        try {
            // Ensure proper date format (YYYY-MM-DD)
            let dateStr = match.match_date;
            if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
                console.log('isMatchFinished: Date format issue, attempting to fix', dateStr);
                const parts = dateStr.split('-');
                if (parts.length === 3) {
                    // Ensure year is 4 digits and month/day are 2 digits
                    const year = parts[0].padStart(4, '20');
                    const month = parts[1].padStart(2, '0');
                    const day = parts[2].padStart(2, '0');
                    dateStr = `${year}-${month}-${day}`;
                }
            }
            
            // Ensure proper time format (HH:MM:SS or HH:MM)
            let timeStr = match.match_time;
            if (!/^\d{1,2}:\d{2}(:\d{2})?$/.test(timeStr)) {
                console.log('isMatchFinished: Time format issue, attempting to fix', timeStr);
                const parts = timeStr.split(':');
                const hours = parts[0].padStart(2, '0');
                const minutes = (parts[1] || '00').padStart(2, '0');
                const seconds = (parts[2] || '00').padStart(2, '0');
                timeStr = `${hours}:${minutes}:${seconds}`;
            }
            
            // Create ISO format date string for reliable parsing
            const isoDateStr = `${dateStr}T${timeStr}${timeStr.length === 5 ? ':00' : ''}`;
            console.log('isMatchFinished: ISO date string:', isoDateStr);
            
            // Parse the date using ISO format
            let matchDateTime = new Date(isoDateStr);
            
            // Check if date is valid
            if (isNaN(matchDateTime.getTime())) {
                console.error('isMatchFinished: Invalid date after parsing:', isoDateStr);
                // Fallback: try direct parsing
                const directParsed = new Date(`${match.match_date} ${match.match_time}`);
                if (isNaN(directParsed.getTime())) {
                    console.error('isMatchFinished: Fallback parsing also failed');
                    return false;
                }
                console.log('isMatchFinished: Fallback parsing successful:', directParsed);
                matchDateTime = directParsed;
            }
            
            const now = new Date();
            console.log('isMatchFinished: Match date/time:', matchDateTime.toISOString());
            console.log('isMatchFinished: Current time:', now.toISOString());
            
            // Add match duration
            const duration = parseInt(match.duration) || 60; // default to 60 minutes if not specified
            console.log('isMatchFinished: Match duration:', duration, 'minutes');
            
            // Create a new date object to avoid mutating matchDateTime
            const matchEndTime = new Date(matchDateTime.getTime() + (duration * 60 * 1000));
            console.log('isMatchFinished: Match end time:', matchEndTime.toISOString());
            
            // Compare current time with match end time
            const isFinished = now > matchEndTime;
            console.log('isMatchFinished: Is match finished?', isFinished);
            
            return isFinished;
        } catch (error) {
            console.error('isMatchFinished: Error calculating match end time:', error);
            return false;
        }
    };

    useEffect(() => {
        // Replace isMatchInPast check with isMatchFinished
        setIsMatchInPast(isMatchFinished());
        
        // Log match details including team_size
        if (match) {
            console.log('Match details loaded in TeamBuilder:', {
                match_id: match.match_id,
                team_size: match.team_size,
                team_type: match.team_type,
                max_players: match.max_players
            });
        }

        // Check if teams data already exists from props and set teamsPublished accordingly
        if (teams && (teams.White?.length > 0 || teams.Colour?.length > 0)) {
            setTeamsPublished(true);
        }
    }, [match, teams, isMatchFinished]);

    // First define getPlayerPosition before it's used
    const getPlayerPosition = (player, team, formation) => {
        const positions = formationPositions[formation];
        if (!positions) return { top: 50, left: 50 };

        // Special handling for 4-part formations
        const formationParts = formation.split('-');
        let def, mid, fwd;
        
        if (formationParts.length === 4) {
            // For formations like 4-2-3-1, use the positions as defined in formationPositions
            // These will have custom positioning for combined midfield lines
        } else {
            // For standard 3-part formations
            [def, mid, fwd] = formationParts.map(Number);
        }

        const basePositions = positions;

        // Extract position type and side from player.position
        const [posType, side] = player.position.split('-');
        
        // Helper function to get position from array or single object
        const getPosition = (posArray, index) => {
            if (!posArray) return { top: 50, left: 50 };
            // If it's a single object (not an array)
            if (!Array.isArray(posArray)) return posArray;
            // If it's an array, return the requested index
            return posArray[index] || posArray[0];
        };

        switch(posType) {
            case 'forward':
                if (side === 'left') return getPosition(basePositions.forward, 0);
                if (side === 'right') return getPosition(basePositions.forward, fwd - 1);
                if (side === 'center') return getPosition(basePositions.forward, Math.floor(fwd/2));
                if (side === 'center-left' && fwd === 4) return getPosition(basePositions.forward, 1);
                if (side === 'center-right' && fwd === 4) return getPosition(basePositions.forward, 2);
                // Default fallback if side is not specified
                if (fwd === 1) return getPosition(basePositions.forward, 0);
                if (fwd === 3 && Array.isArray(basePositions.forward) && basePositions.forward.length === 3) {
                    return basePositions.forward[1]; // Get the middle position for center
                }
                return getPosition(basePositions.forward, Math.floor(fwd/2));
                
            case 'midfield':
                // Specific handling for 4 midfielders
                if (mid === 4) {
                    if (side === 'left') return getPosition(basePositions.midfield, 0);
                    if (side === 'center-left') return getPosition(basePositions.midfield, 1);
                    if (side === 'center-right') return getPosition(basePositions.midfield, 2);
                    if (side === 'right') return getPosition(basePositions.midfield, 3);
                }
                // Handle 5 midfielders (for formations like 4-2-3-1)
                if (mid === 5) {
                    if (side === 'left') return getPosition(basePositions.midfield, 0);
                    if (side === 'center-left') return getPosition(basePositions.midfield, 1);
                    if (side === 'center') return getPosition(basePositions.midfield, 2);
                    if (side === 'center-right') return getPosition(basePositions.midfield, 3);
                    if (side === 'right') return getPosition(basePositions.midfield, 4);
                }
                // Standard handling for other formations
                if (side === 'left') return getPosition(basePositions.midfield, 0);
                if (side === 'right') return getPosition(basePositions.midfield, mid - 1);
                if (side === 'center') return getPosition(basePositions.midfield, Math.floor(mid/2));
                // Default fallback if side is not specified
                if (mid === 1) return getPosition(basePositions.midfield, 0);
                if (mid === 3 && Array.isArray(basePositions.midfield) && basePositions.midfield.length === 3) {
                    return basePositions.midfield[1]; // Get the middle position for center
                }
                return getPosition(basePositions.midfield, Math.floor(mid/2));
                
            case 'defense':
                // Specific handling for 4 defenders
                if (def === 4) {
                    if (side === 'left') return getPosition(basePositions.defense, 0);
                    if (side === 'center-left') return getPosition(basePositions.defense, 1);
                    if (side === 'center-right') return getPosition(basePositions.defense, 2);
                    if (side === 'right') return getPosition(basePositions.defense, 3);
                }
                // Specific handling for 5 defenders
                if (def === 5) {
                    if (side === 'left') return getPosition(basePositions.defense, 0);
                    if (side === 'center-left') return getPosition(basePositions.defense, 1);
                    if (side === 'center') return getPosition(basePositions.defense, 2);
                    if (side === 'center-right') return getPosition(basePositions.defense, 3);
                    if (side === 'right') return getPosition(basePositions.defense, 4);
                }
                // Standard handling for other formations
                if (side === 'left') return getPosition(basePositions.defense, 0);
                if (side === 'right') return getPosition(basePositions.defense, def - 1);
                if (side === 'center') return getPosition(basePositions.defense, Math.floor(def/2));
                // Default fallback if side is not specified
                if (def === 1) return getPosition(basePositions.defense, 0);
                if (def === 3 && Array.isArray(basePositions.defense) && basePositions.defense.length === 3) {
                    return basePositions.defense[1]; // Get the middle position for center
                }
                return getPosition(basePositions.defense, Math.floor(def/2));
                
            case 'goalkeeper':
                // Return goalkeeper position if defined in formation
                if (basePositions.goalkeeper) {
                    return basePositions.goalkeeper;
                }
                // Default goalkeeper position
                return { top: 85, left: 50 };
                
            default:
                console.error(`Invalid position type: ${posType} for player`, player);
                return { top: 50, left: 50 };
        }
    };

    // Add function for safely getting team names with fallbacks
    const getTeamName = (team) => {
        if (team === 'white') return teamWhiteName;
        if (team === 'colour') return teamColourName;
        return team === 'white' ? 'Whites' : 'Colours';
    };

    // Then define player states that use getPlayerPosition
    const [whitePlayers, setWhitePlayers] = React.useState([]);
    const [colourPlayers, setColourPlayers] = React.useState(needsTwoTeams ? [] : []);
    // Update the initial playerPool state to be empty
    const [playerPool, setPlayerPool] = React.useState([]);

    // Get available formations or use defaults
    const getAvailableFormations = (playersCount) => {
        // Check if we have a team_size format from the match
        const teamSizeFormat = match?.team_size;
        
        // Special handling for 11v11 format
        if (teamSizeFormat && teamSizeFormat.replace(/\s+/g, '').toLowerCase() === '11v11') {
            console.log(`Using 11v11 formations from getAvailableFormations`);
            if (formations[10]) {
                return formations[10];
            }
        }
        
        const outfieldPlayersPerTeam = getTeamSizeFormat(teamSizeFormat);
        
        // If we have a valid team size format, use it
        if (outfieldPlayersPerTeam && formations[outfieldPlayersPerTeam]) {
            console.log(`Using formatted team size: ${teamSizeFormat} (${outfieldPlayersPerTeam} outfield players)`);
            return formations[outfieldPlayersPerTeam];
        }
        
        // Otherwise fall back to the original logic
        // Determine number of players per team (rounded up to ensure we have enough positions)
        const playersPerTeam = Math.ceil(playersCount / 2);
        
        console.log(`Calculated players per team: ${playersPerTeam} from ${playersCount} total players`);
        
        // For 4v4, we need formations for 3 outfield players per team (plus goalkeeper)
        // For 5v5, we need formations for 4 outfield players per team (plus goalkeeper)
        // For 6v6, we need formations for 5 outfield players per team (plus goalkeeper)
        // etc.
        
        // First try to get from formations array
        if (formations[playersPerTeam - 1]) {
            return formations[playersPerTeam - 1];
        }
        
        // If not found, try to get from defaultFormations based on team size
        if (defaultFormations[playersPerTeam - 1]) {
            return defaultFormations[playersPerTeam - 1];
        }
        
        // Special case for 11v11 (10 outfield players)
        if (playersPerTeam >= 10) {
            console.log(`Using 11v11 formations as fallback for ${playersPerTeam} players per team`);
            if (formations[10]) {
                return formations[10];
            }
        }
        
        // If still not found, use the default fallback
        console.log(`Using default formations`);
        return defaultFormations.default;
    };

    // Instead of using maxPlayers directly, we'll calculate based on actual player count
    const totalPlayers = playerPool.length + whitePlayers.length + colourPlayers.length;
    const availableFormations = getAvailableFormations(totalPlayers);

    // Update allPlayers to use state
    const allPlayers = [
        ...whitePlayers.map(p => ({ ...p, team: 'white' })),
        ...colourPlayers.map(p => ({ ...p, team: 'colour' }))
    ];

    // Add a function to get form multiplier
    const getFormMultiplier = (player) => {
        // Default form multiplier if no history is available
        if (!player.formRating) return 1.0;
        
        // Apply multipliers based on form rating
        switch(player.formRating) {
            case 'Excellent':
                return 1.2;
            case 'Good':
                return 1.1;
            case 'Average':
                return 1.0;
            case 'Rusty':
                return 0.8;
            case 'Unknown':
            default:
                return 1.0;
        }
    };

    const calculatePositionScore = (player, targetPosition) => {
        if (!player || !targetPosition) return 0;

        // Base weights for different attributes based on position
        const weights = {
            'forward': {
                shooting: 0.35,
                pace: 0.25,
                dribbling: 0.20,
                passing: 0.15,
                physical: 0.05,
                defending: 0
            },
            'midfield': {
                passing: 0.30,
                dribbling: 0.25,
                shooting: 0.15,
                defending: 0.15,
                pace: 0.10,
                physical: 0.05
            },
            'defense': {
                defending: 0.35,
                physical: 0.25,
                pace: 0.20,
                passing: 0.15,
                dribbling: 0.05,
                shooting: 0
            }
        };

        // Position match multipliers
        const positionMultipliers = {
            'forward': {
                'FWD': 1.2,
                'MID': 0.8,
                'DEF': 0.6,
                'GK': 0.4
            },
            'midfield': {
                'MID': 1.2,
                'FWD': 0.9,
                'DEF': 0.9,
                'GK': 0.5
            },
            'defense': {
                'DEF': 1.2,
                'MID': 0.8,
                'FWD': 0.6,
                'GK': 1.0
            }
        };

        // Get weights for target position
        const positionWeights = weights[targetPosition];
        if (!positionWeights) return 0;
        
        // Calculate weighted score
        let score = 0;
        let totalWeight = 0;
        
        for (const [attribute, weight] of Object.entries(positionWeights)) {
            const value = parseInt(player[attribute]) || 0;
            score += (value / 100) * weight; // Divide by 100 since stats are 0-100
            totalWeight += weight;
        }

        // Normalize score to percentage
        score = (score / totalWeight) * 100;

        // Apply position multiplier
        const multiplier = positionMultipliers[targetPosition][player.position_db] || 0.5;
        score *= multiplier;
        
        // Apply form multiplier if available
        if (player.history && player.history.formRating) {
            const formMultiplier = getFormMultiplier(player.history);
            score *= formMultiplier;
        }

        return Math.min(100, Math.round(score));
    };

    const assignPlayersToFormation = (players, formation) => {
        // Parse formation to get player counts by position
        const formationParts = formation.split('-').map(Number);
        
        // Handle different formation structures
        let defNum, midNum, fwdNum;
        
        if (formationParts.length === 4) {
            // Special handling for 4-part formations like 4-2-3-1
            defNum = formationParts[0];
            midNum = formationParts[1] + formationParts[2]; // Combine the two midfield layers
            fwdNum = formationParts[3];
            console.log(`Processing 4-part formation: ${formation} as def:${defNum}, mid:${midNum}, fwd:${fwdNum}`);
        } else {
            // Regular 3-part formation
            [defNum, midNum, fwdNum] = formationParts;
        }
        
        let availablePlayers = [...players];
        const assignedPlayers = [];

        // Helper function to get best player for a position
        const getBestPlayerForPosition = (position, count) => {
            // Score all available players for this position
            const scoredPlayers = availablePlayers.map(player => {
                // Calculate position score, which now includes form consideration
                const positionScore = calculatePositionScore(player, position);
                
                return {
                ...player,
                    score: positionScore
                };
            });

            // Sort by score descending
            scoredPlayers.sort((a, b) => b.score - a.score);

            // Take the best players for this position
            const bestPlayers = scoredPlayers.slice(0, count);

            // Remove selected players from available pool
            bestPlayers.forEach(selected => {
                availablePlayers = availablePlayers.filter(p => p.id !== selected.id);
            });

            return bestPlayers;
        };

        // Check if we need a goalkeeper based on permanent_keeper setting
        const permanentKeeper = sessionStorage.getItem('permanent_keeper');
        const needsGoalkeeper = permanentKeeper !== null && permanentKeeper !== "0";
        console.log(`Permanent keeper setting: ${permanentKeeper}, needs goalkeeper: ${needsGoalkeeper}`);
        
        // Only assign a goalkeeper if the setting is enabled
        if (needsGoalkeeper) {
            // Look for players with GK position or highest defending score
            const gkPlayers = availablePlayers.filter(p => 
                p.position_db === 'GK' || 
                p.position === 'GK' || 
                p.position?.toLowerCase().includes('keeper')
            );
            
            let goalkeeper;
            if (gkPlayers.length > 0) {
                // Choose the first goalkeeper in the list
                goalkeeper = gkPlayers[0];
                // Remove from available players
                availablePlayers = availablePlayers.filter(p => p.id !== goalkeeper.id);
            } else {
                // If no dedicated goalkeeper, use the player with highest defending skill
                const scoredDefenders = availablePlayers.map(player => ({
                    ...player,
                    score: parseInt(player.defending) || 0
                })).sort((a, b) => b.score - a.score);
                
                if (scoredDefenders.length > 0) {
                    goalkeeper = scoredDefenders[0];
                    availablePlayers = availablePlayers.filter(p => p.id !== goalkeeper.id);
                }
            }
            
            // Add goalkeeper to assigned players
            if (goalkeeper) {
                assignedPlayers.push({
                    ...goalkeeper,
                    position: 'goalkeeper'
                });
            }
        }

        // 1. Then assign forwards (to get best shooters in attack)
        const forwards = getBestPlayerForPosition('forward', fwdNum);
        forwards.forEach((player, index) => {
            let position = 'forward-center';
            if (fwdNum === 2) {
                position = index === 0 ? 'forward-left' : 'forward-right';
            } else if (fwdNum === 3) {
                position = index === 0 ? 'forward-left' : 
                          index === 1 ? 'forward-center' : 'forward-right';
            } else if (fwdNum === 4) {
                // Handle 4 forwards (for 9v9)
                position = index === 0 ? 'forward-left' : 
                           index === 1 ? 'forward-center-left' : 
                           index === 2 ? 'forward-center-right' : 'forward-right';
            }
            assignedPlayers.push({
                ...player,
                position
            });
        });

        // 2. Then assign defenders (to keep best defenders back)
        const defenders = getBestPlayerForPosition('defense', defNum);
        defenders.forEach((player, index) => {
            let position = 'defense-center';
            if (defNum === 2) {
                position = index === 0 ? 'defense-left' : 'defense-right';
            } else if (defNum === 3) {
                position = index === 0 ? 'defense-left' : 
                          index === 1 ? 'defense-center' : 'defense-right';
            } else if (defNum === 4) {
                // Handle 4 defenders (for 9v9)
                position = index === 0 ? 'defense-left' : 
                           index === 1 ? 'defense-center-left' : 
                           index === 2 ? 'defense-center-right' : 'defense-right';
            } else if (defNum === 5) {
                // Handle 5 defenders (for 5-3-2 formation)
                position = index === 0 ? 'defense-left' : 
                           index === 1 ? 'defense-center-left' : 
                           index === 2 ? 'defense-center' :
                           index === 3 ? 'defense-center-right' : 'defense-right';
            }
            assignedPlayers.push({
                ...player,
                position
            });
        });

        // 3. Finally, fill midfield with remaining players
        const midfielders = getBestPlayerForPosition('midfield', midNum);
        midfielders.forEach((player, index) => {
            let position = 'midfield-center';
            if (midNum === 2) {
                position = index === 0 ? 'midfield-left' : 'midfield-right';
            } else if (midNum === 3) {
                position = index === 0 ? 'midfield-left' : 
                          index === 1 ? 'midfield-center' : 'midfield-right';
            } else if (midNum === 4) {
                // Handle 4 midfielders (for 9v9)
                position = index === 0 ? 'midfield-left' : 
                           index === 1 ? 'midfield-center-left' : 
                           index === 2 ? 'midfield-center-right' : 'midfield-right';
            } else if (midNum === 5) {
                // Handle 5 midfielders (for formations like 4-2-3-1 which we treat as 4-5-1)
                position = index === 0 ? 'midfield-left' : 
                           index === 1 ? 'midfield-center-left' : 
                           index === 2 ? 'midfield-center' :
                           index === 3 ? 'midfield-center-right' : 'midfield-right';
            }
            assignedPlayers.push({
                ...player,
                position
            });
        });

        // No substitutes are assigned here - they remain in the player pool

        return assignedPlayers;
    };

    // Update the formation change handlers
    const handleWhiteFormationChange = (e) => {
        const newFormation = e.target.value;
        
        // Calculate the expected player count for this formation
        const formationParts = newFormation.split('-').map(Number);
        const outfieldPlayers = formationParts.reduce((sum, count) => sum + count, 0);
        
        // Get actual player count
        const totalAvailablePlayers = playerPool.length + whitePlayers.length + colourPlayers.length;
        const actualPlayersPerTeam = Math.floor(totalAvailablePlayers / 2);
        
        // Check if this formation is suitable for the current player count
        const permanentKeeper = sessionStorage.getItem('permanent_keeper');
        const usesDedicatedKeeper = permanentKeeper !== null && permanentKeeper !== "0";
        
        let isFormationValid = true;
        
        if (usesDedicatedKeeper) {
            // In keeper mode, outfield players should be actualPlayersPerTeam - 1
            if (outfieldPlayers !== actualPlayersPerTeam - 1) {
                console.log(`Warning: Formation ${newFormation} requires ${outfieldPlayers} outfield players, but we have ${actualPlayersPerTeam - 1}`);
                // Don't block the change, but log a warning
            }
        } else {
            // In non-keeper mode, special case for 8-player teams using 7-a-side formations
            if (actualPlayersPerTeam === 8 && outfieldPlayers === 7) {
                console.log(`Special case: Using 7-a-side formation ${newFormation} for 8-player team without keeper`);
                // This is valid for 8-player teams
            } else if (outfieldPlayers !== actualPlayersPerTeam) {
                console.log(`Warning: Formation ${newFormation} requires ${outfieldPlayers} players, but we have ${actualPlayersPerTeam}`);
                // Don't block the change, but log a warning
            }
        }
        
        const redistributedPlayers = assignPlayersToFormation(whitePlayers, newFormation);
        setWhitePlayers(redistributedPlayers);
        setWhiteFormation(newFormation);
    };

    const handleColourFormationChange = (e) => {
        const newFormation = e.target.value;
        
        // Calculate the expected player count for this formation
        const formationParts = newFormation.split('-').map(Number);
        const outfieldPlayers = formationParts.reduce((sum, count) => sum + count, 0);
        
        // Get actual player count
        const totalAvailablePlayers = playerPool.length + whitePlayers.length + colourPlayers.length;
        const actualPlayersPerTeam = Math.floor(totalAvailablePlayers / 2);
        
        // Check if this formation is suitable for the current player count
        const permanentKeeper = sessionStorage.getItem('permanent_keeper');
        const usesDedicatedKeeper = permanentKeeper !== null && permanentKeeper !== "0";
        
        let isFormationValid = true;
        
        if (usesDedicatedKeeper) {
            // In keeper mode, outfield players should be actualPlayersPerTeam - 1
            if (outfieldPlayers !== actualPlayersPerTeam - 1) {
                console.log(`Warning: Formation ${newFormation} requires ${outfieldPlayers} outfield players, but we have ${actualPlayersPerTeam - 1}`);
                // Don't block the change, but log a warning
            }
        } else {
            // In non-keeper mode, special case for 8-player teams using 7-a-side formations
            if (actualPlayersPerTeam === 8 && outfieldPlayers === 7) {
                console.log(`Special case: Using 7-a-side formation ${newFormation} for 8-player team without keeper`);
                // This is valid for 8-player teams
            } else if (outfieldPlayers !== actualPlayersPerTeam) {
                console.log(`Warning: Formation ${newFormation} requires ${outfieldPlayers} players, but we have ${actualPlayersPerTeam}`);
                // Don't block the change, but log a warning
            }
        }
        
        const redistributedPlayers = assignPlayersToFormation(colourPlayers, newFormation);
        setColourPlayers(redistributedPlayers);
        setColourFormation(newFormation);
    };

    // Add this function to calculate position group stats
    const calculatePositionGroupStats = (players, positionType) => {
        // Adjust the filter to handle both spellings for defence/defense
        const positionPlayers = players.filter(p => {
            if (positionType === 'defense') {
                return p.position.includes('defence') || p.position.includes('defense');
            }
            return p.position.includes(positionType);
        });
        
        if (!positionPlayers.length) return { overall: 0 };

        const stats = positionPlayers.reduce((acc, player) => {
            // Convert string values to numbers, use 0 if undefined
            const pace = parseInt(player.pace) || 0;
            const shooting = parseInt(player.shooting) || 0;
            const passing = parseInt(player.passing) || 0;
            const dribbling = parseInt(player.dribbling) || 0;
            const defending = parseInt(player.defending) || 0;
            const physical = parseInt(player.physical) || 0;

            // Calculate position-specific weighted averages
            let overall;
            if (positionType === 'forward') {
                overall = (shooting * 0.4 + pace * 0.2 + dribbling * 0.2 + passing * 0.1 + physical * 0.1);
            } else if (positionType === 'midfield') {
                overall = (passing * 0.3 + dribbling * 0.2 + shooting * 0.2 + defending * 0.1 + pace * 0.1 + physical * 0.1);
            } else { // defense
                overall = (defending * 0.4 + physical * 0.2 + pace * 0.2 + passing * 0.1 + dribbling * 0.1);
            }

            return {
                count: acc.count + 1,
                overall: acc.overall + overall
            };
        }, { count: 0, overall: 0 });

        return {
            overall: (stats.overall / stats.count).toFixed(1)
        };
    };

    // Add this helper function to get the relevant stat for a position
    const getRelevantStat = (player, position) => {
        if (position.includes('forward')) {
            return { label: 'SHO', value: player.shooting };
        } else if (position.includes('midfield')) {
            return { label: 'PAS', value: player.passing };
        } else if (position.includes('defense')) {
            return { label: 'DEF', value: player.defending };
        }
        return { label: '', value: '' };
    };

    // Update the player display to show all three key stats
    const getPlayerStats = (player) => {
        return [
            { label: 'SHO', value: player.shooting },
            { label: 'PAS', value: player.passing },
            { label: 'DEF', value: player.defending }
        ];
    };

    // Add function to evaluate formation score
    const evaluateFormation = (players, formation) => {
        const formationParts = formation.split('-').map(Number);
        
        // Handle different formation structures
        let defNum, midNum, fwdNum;
        
        if (formationParts.length === 4) {
            // Special handling for 4-part formations like 4-2-3-1
            defNum = formationParts[0];
            midNum = formationParts[1] + formationParts[2]; // Combine the two midfield layers
            fwdNum = formationParts[3];
        } else {
            // Regular 3-part formation
            [defNum, midNum, fwdNum] = formationParts;
        }
        
        let score = 0;

        // Simulate player assignments for this formation
        const simulatedPlayers = assignPlayersToFormation([...players], formation);
        
        // Score forwards
        const forwards = simulatedPlayers.filter(p => p.position.includes('forward'));
        const forwardScore = forwards.reduce((acc, player) => {
            return acc + parseInt(player.shooting) + parseInt(player.pace);
        }, 0) / (fwdNum || 1);

        // Score midfielders
        const midfielders = simulatedPlayers.filter(p => p.position.includes('midfield'));
        const midfieldScore = midfielders.reduce((acc, player) => {
            return acc + parseInt(player.passing) + parseInt(player.dribbling);
        }, 0) / (midNum || 1);

        // Score defenders
        const defenders = simulatedPlayers.filter(p => p.position.includes('defense'));
        const defenseScore = defenders.reduce((acc, player) => {
            return acc + parseInt(player.defending) + parseInt(player.physical);
        }, 0) / (defNum || 1);

        // Weight the scores based on formation balance
        score = (forwardScore * fwdNum + midfieldScore * midNum + defenseScore * defNum) / (fwdNum + midNum + defNum);

        // Add bonus for natural positions
        const naturalPositionBonus = simulatedPlayers.reduce((acc, player) => {
            if ((player.position.includes('forward') && player.position_db === 'FWD') ||
                (player.position.includes('midfield') && player.position_db === 'MID') ||
                (player.position.includes('defense') && player.position_db === 'DEF')) {
                return acc + 10;
            }
            return acc;
        }, 0);

        return {
            formation,
            score: score + naturalPositionBonus,
            forwardScore,
            midfieldScore,
            defenseScore
        };
    };

    // Add function to find best formation
    const findBestFormation = (players, formations) => {
        const formationScores = formations.map(formation => 
            evaluateFormation(players, formation)
        );

        // Sort by total score
        formationScores.sort((a, b) => b.score - a.score);
        
        return formationScores[0].formation;
    };

    // Update the analyzePlayerForm function to use the data directly from playerDetails
    const getPlayerFormData = (player) => {
        // If player details include form data, use it
        if (player.form_rating) {
            return {
                recentMatches: player.matches_played_90days || 0,
                lastPlayed: player.last_played_date || 'Never',
                daysSinceLastMatch: player.days_since_last_match || 999,
                winRate: player.win_rate || 0,
                goals: player.recent_goals || 0,
                motm: player.recent_motm || 0,
                formDescription: player.form_description || 'No recent data',
                formRating: player.form_rating || 'Unknown',
                matchHistory: player.match_history || [],
                matchSummary: player.match_summary || 'No matches',
                matchResults: player.match_results || []
            };
        }
        
        // Fallback to default values if form data is not available
        return {
            recentMatches: 0,
            lastPlayed: 'Never',
            daysSinceLastMatch: 999,
            winRate: 0,
            goals: 0,
            motm: 0,
            formDescription: 'No recent data',
            formRating: 'Unknown',
            matchHistory: [],
            matchSummary: 'No matches',
            matchResults: []
        };
    };

    // Add state for loading indicator
    const [isSorting, setIsSorting] = React.useState(false);
    const [analysisMessage, setAnalysisMessage] = React.useState('');

    // Add state for tracking progress steps
    const [analysisStep, setAnalysisStep] = React.useState(1);
    const totalSteps = 4;

    // Helper function to calculate how many players are needed for a formation
    const getPlayersNeededForFormation = (formation) => {
        if (!formation) return 0;
        
        // A formation like 4-4-2 needs exactly 11 players (10 outfield + 1 goalkeeper)
        const formationParts = formation.split('-').map(Number);
        let def, mid, fwd;
        
        if (formationParts.length === 4) {
            // Special handling for 4-part formations like 4-2-3-1
            def = formationParts[0];
            mid = formationParts[1] + formationParts[2]; 
            fwd = formationParts[3];
        } else {
            // Regular 3-part formation
            [def, mid, fwd] = formationParts;
        }
        
        // Total outfield players in this formation
        const outfieldPlayers = def + mid + fwd;
        
        // Check permanent_keeper setting in session storage
        const permanentKeeper = sessionStorage.getItem('permanent_keeper');
        
        // If permanent_keeper is not set or is "0", we don't need a dedicated keeper position
        if (permanentKeeper === null || permanentKeeper === "0") {
            // Special case for 8-player teams with formations designed for 7 outfield players
            const totalAvailablePlayers = playerPool.length + whitePlayers.length + colourPlayers.length;
            const actualPlayersPerTeam = Math.floor(totalAvailablePlayers / 2);
            
            if (actualPlayersPerTeam === 8 && outfieldPlayers === 7) {
                console.log(`Special case: 7-a-side formation ${formation} being used for 8-player team without keeper`);
                return 8; // This 7-a-side formation is actually for 8 players total in no-keeper mode
            }
            
            return outfieldPlayers; // Just the outfield players, no dedicated keeper
        }
        
        // Otherwise, include a goalkeeper position
        return outfieldPlayers + 1; // +1 for goalkeeper
    };

    // Update the sortPlayersIntoTeams function to keep subs in player pool
    const sortPlayersIntoTeams = async () => {
        setIsLoading(true);
        setIsSorting(true); // Add this line to enable the animation
        setAnalysisStep(1); // Add this line to reset the step counter
        
        try {
            // Check if we have enough players to sort
            const allAvailablePlayers = [...playerPool];
            console.log("All available players:", allAvailablePlayers);
            
            if (allAvailablePlayers.length < 2) {
                console.log("Not enough players to sort");
                setIsLoading(false);
                setIsSorting(false); // Add this line for cleanup
                return;
            }
            
            // Add delay for step 1
            await new Promise(resolve => setTimeout(resolve, 800));
            setAnalysisMessage("Analyzing player data and statistics...");
            
            // Randomize the player order
            const shuffledPlayers = [...allAvailablePlayers].sort(() => Math.random() - 0.5);
            
            // Sort players by playing form if sufficient data exists
            const sortedPlayers = shuffledPlayers.map(player => {
                // Calculate a form rating for the player if we have stats
                const formData = getPlayerFormData(player);
                return { ...player, formData };
            });
            
            // Step 2: Team balancing
            await new Promise(resolve => setTimeout(resolve, 1000));
            setAnalysisStep(2);
            setAnalysisMessage("Building balanced teams...");
            
            // Get the right number of players based on team_size or max_players
            let playersToSort = sortedPlayers;
            
            // Check if match has a specific team_size format (like 5v5, 7v7, etc.)
            const teamSizeFormat = match?.team_size;
            let playersPerTeam = null;
            let minimumRequiredPlayers = null;
            
            if (teamSizeFormat) {
                console.log(`Match has team_size specified: ${teamSizeFormat}`);
                
                // Handle specific case for 11v11 which requires special handling
                if (teamSizeFormat.replace(/\s+/g, '').toLowerCase() === '11v11') {
                    playersPerTeam = 11; // 11 players per team for 11v11 (including GK)
                    console.log(`Using 11v11 format: ${playersPerTeam} players per team`);
                } else {
                    // Extract total players needed from format (for both teams)
                    const cleanFormat = teamSizeFormat.replace(/\s+/g, '');
                    const playersPerSide = parseInt(cleanFormat.split('v')[0]);
                    if (!isNaN(playersPerSide)) {
                        // This represents how many players we need per team, including GK
                        playersPerTeam = playersPerSide;
                        console.log(`Using team size from match: ${teamSizeFormat} (${playersPerTeam} players per team)`);
                    }
                }
                
                // Calculate minimum required players (must have at least GK + outfield players)
                if (playersPerTeam) {
                    if (needsTwoTeams) {
                        // For two teams, we need at least the minimum number per team × 2
                        minimumRequiredPlayers = playersPerTeam * 2;
                    } else {
                        // For one team, we just need the minimum number for that team
                        minimumRequiredPlayers = playersPerTeam;
                    }
                }
            }
            
            // If not specified by team_size, use max_players from match if available
            if (!playersPerTeam && match && match.max_players) {
                // For academy/squad matches, each team needs half the max players
                if (['academy', 'squad'].includes(selectedTeamType)) {
                    playersPerTeam = Math.floor(match.max_players / 2);
                    console.log(`Using max_players: ${match.max_players} (${playersPerTeam} players per team)`);
                } 
                // For friendly matches, use actual player count instead of max_players
                else {
                    // Calculate based on actual available players instead of max_players
                    playersPerTeam = Math.floor(allAvailablePlayers.length / 2);
                    console.log(`Using actual player count: ${allAvailablePlayers.length} (${playersPerTeam} players per team)`);
                }
            }
            
            // If we still don't have a firm number, default based on player count
            if (!playersPerTeam) {
                // Default to half the available players, at least 5 per team (for a standard 5-a-side)
                playersPerTeam = Math.max(5, Math.floor(sortedPlayers.length / 2));
                console.log(`Using default team size: ${playersPerTeam} players per team`);
            }
            
            // IMPORTANT: Check if we have enough players for the required format
            if (minimumRequiredPlayers && sortedPlayers.length < minimumRequiredPlayers) {
                console.log(`Warning: Not enough players for ${teamSizeFormat} format. Need ${minimumRequiredPlayers}, have ${sortedPlayers.length}`);
                // Still continue with available players, but log the warning
            }
            
            // Handle different team size formats
            console.log(`Processing team size: ${teamSizeFormat || 'Not specified'}`);
            const outfieldPlayersPerTeam = getTeamSizeFormat(teamSizeFormat);
            
            // Determine the best formation for each team based on the player count and format
            let formationIndex = outfieldPlayersPerTeam;
            
            // Handle 11v11 special case
            if (teamSizeFormat && teamSizeFormat.replace(/\s+/g, '').toLowerCase() === '11v11') {
                formationIndex = 10; // Use 10 outfield players for 11v11
            } 
            // Handle academy/squad mode (single team)
            else if (!needsTwoTeams) {
                // For academy/squad, we use the configured team size or all available players
                let academyPlayersCount;
                
                if (playersPerTeam) {
                    // Use the configured team size if available
                    academyPlayersCount = playersPerTeam;
                } else {
                    // Otherwise use all available players
                    academyPlayersCount = allAvailablePlayers.length;
                }
                
                // Check if permanent keeper mode is enabled
                const permanentKeeper = sessionStorage.getItem('permanent_keeper');
                const usesDedicatedKeeper = permanentKeeper !== null && permanentKeeper !== "0";
                
                // For keeper mode enabled, we need one less outfield player
                formationIndex = usesDedicatedKeeper ? academyPlayersCount - 2 : academyPlayersCount - 1;
                
                console.log(`Academy/Squad mode: Using formation index ${formationIndex} for ${academyPlayersCount} players (keeper mode: ${usesDedicatedKeeper ? 'enabled' : 'disabled'})`);
            }
            // For friendly matches, override formationIndex based on actual players per team 
            else if (needsTwoTeams) {
                // Calculate players per team based on actual count
                const actualPlayersPerTeam = Math.floor(allAvailablePlayers.length / 2);
                
                // Check if permanent keeper mode is enabled
                const permanentKeeper = sessionStorage.getItem('permanent_keeper');
                const usesDedicatedKeeper = permanentKeeper !== null && permanentKeeper !== "0";
                
                // We always need to use formationIndex = actualPlayersPerTeam - 1
                // This is because formation indexes in formations.js are based on outfield players
                formationIndex = usesDedicatedKeeper ? actualPlayersPerTeam - 2 : actualPlayersPerTeam - 1;
                
                console.log(`Friendly mode: Adjusted formation index to ${formationIndex} based on actual player count (keeper mode: ${usesDedicatedKeeper ? 'enabled' : 'disabled'})`);
                
                // IMPORTANT FIX: Check if team_size format requires more players than we actually have
                const configuredTeamSize = getTeamSizeFormat(teamSizeFormat);
                if (configuredTeamSize && configuredTeamSize > actualPlayersPerTeam) {
                    console.log(`WARNING: Match configured for ${teamSizeFormat} but only have ${actualPlayersPerTeam} players per team`);
                    console.log(`Overriding formation selection to use actual player count instead of configured team size`);
                    
                    // Force using formations based on actual player count, not configured team size
                    // This prevents selecting formations that need more players than we have
                }
            }
            
            // Get available formations for the appropriate team size
            let playerFormations = [];
            if (formationIndex && formations[formationIndex]) {
                playerFormations = formations[formationIndex];
                console.log(`Using formations for ${formationIndex} outfield players:`, playerFormations);
            } else {
                // If we don't have formations for the exact player count, find the closest available
                const availableFormationKeys = Object.keys(formations).map(Number).sort((a, b) => a - b);
                const closestKey = availableFormationKeys.reduce((prev, curr) => 
                    Math.abs(curr - formationIndex) < Math.abs(prev - formationIndex) ? curr : prev
                );
                
                if (closestKey && formations[closestKey]) {
                    playerFormations = formations[closestKey];
                    console.log(`No formations for ${formationIndex} players, using closest match (${closestKey}):`, playerFormations);
                } else {
                    // Final fallback to available formations based on player count
                    playerFormations = getAvailableFormations(playersToSort.length);
                    console.log(`Using default formations for ${playersToSort.length} players:`, playerFormations);
                }
            }
            
            // Select best formations based on team size
            let bestWhiteFormation = playerFormations[0];
            let bestColourFormation = playerFormations[0];
            
            // Use optimal formations if possible based on player skills
            if (playersToSort.length >= 10) {
                const halfPlayers = Math.floor(playersToSort.length / 2);
                
                // Find best formation for white team, using half the players
                const optimalWhiteFormation = findOptimalFormation(playersToSort.slice(0, halfPlayers));
                if (optimalWhiteFormation && playerFormations.includes(optimalWhiteFormation)) {
                    bestWhiteFormation = optimalWhiteFormation;
                }
                
                // Find best formation for colour team, using the other half
                const optimalColourFormation = findOptimalFormation(playersToSort.slice(halfPlayers));
                if (optimalColourFormation && playerFormations.includes(optimalColourFormation)) {
                    bestColourFormation = optimalColourFormation;
                }
            }
            
            // Set the formations
            setWhiteFormation(bestWhiteFormation);
            if (needsTwoTeams) {
                setColourFormation(bestColourFormation);
            }
            
            // Sort players by overall skill (simplified for this example)
            const playersBySkill = [...sortedPlayers].sort((a, b) => {
                const scoreA = a.formData ? a.formData.overallScore : 50;
                const scoreB = b.formData ? b.formData.overallScore : 50;
                return scoreB - scoreA; // Sort high to low
            });
            
            const whitePlacesToFill = getPlayersNeededForFormation(bestWhiteFormation);
            const colourPlacesToFill = needsTwoTeams ? getPlayersNeededForFormation(bestColourFormation) : 0;
            
            console.log(`White team needs exactly ${whitePlacesToFill} players for formation ${bestWhiteFormation}`);
            if (needsTwoTeams) {
                console.log(`Colour team needs exactly ${colourPlacesToFill} players for formation ${bestColourFormation}`);
            }
            
            // Calculate total players needed
 // Removed unused totalPlayersNeeded variable
            
            // CRITICAL FIX: If the selected formations require more players than we have per team, adjust formations
            const playersPerSide = Math.floor(playersBySkill.length / 2);
            const isFormationTooLarge = needsTwoTeams && 
                ((whitePlacesToFill > playersPerSide) || (colourPlacesToFill > playersPerSide));
                
            if (isFormationTooLarge) {
                console.log(`FIXING ISSUE: Selected formations (${bestWhiteFormation}/${bestColourFormation}) need more players than available`);
                
                // Find formations that match the actual player count
                const permanentKeeperSetting = sessionStorage.getItem('permanent_keeper');
                const usesDedicatedKeeper = permanentKeeperSetting !== null && permanentKeeperSetting !== "0";
                
                // The actual number of outfield players we can have per team
                // With a dedicated keeper, we have one less outfield player
                const correctOutfieldPlayerCount = usesDedicatedKeeper ? playersPerSide - 1 : playersPerSide;
                
                console.log(`Looking for formations for ${correctOutfieldPlayerCount} outfield players per team (keeper mode: ${usesDedicatedKeeper ? 'enabled' : 'disabled'})`);
                
                // Get the appropriate formations 
                if (formations[correctOutfieldPlayerCount]) {
                    // Found formations that exactly match our player count
                    const correctFormations = formations[correctOutfieldPlayerCount];
                    
                    // CRITICAL ADDITIONAL FIX: Verify that the formations actually use the correct number of players
                    // This prevents selecting formations with too many positions
                    const validFormations = correctFormations.filter(formation => {
                        const formationParts = formation.split('-').map(Number);
                        const outfieldPlayers = formationParts.reduce((sum, count) => sum + count, 0);
                        return outfieldPlayers === correctOutfieldPlayerCount; // Must match outfield player count
                    });
                    
                    if (validFormations.length > 0) {
                        console.log(`Found ${validFormations.length} formations that exactly match ${correctOutfieldPlayerCount} outfield players:`, validFormations);
                        bestWhiteFormation = validFormations[0];
                        bestColourFormation = validFormations[0];
                    } else {
                        // If no exact matches, try to create a valid formation
                        console.log(`No formations found that exactly match ${correctOutfieldPlayerCount} outfield players. Creating custom formation...`);
                        
                        // Create a balanced formation based on the actual number of players
                        const playerCountPerPosition = Math.floor(correctOutfieldPlayerCount / 3);
                        const remainder = correctOutfieldPlayerCount % 3;
                        
                        let def = playerCountPerPosition;
                        let mid = playerCountPerPosition;
                        let fwd = playerCountPerPosition;
                        
                        // Distribute remainder
                        if (remainder === 1) {
                            mid += 1; // Add extra to midfield
                        } else if (remainder === 2) {
                            def += 1; // Add one to defense
                            fwd += 1; // Add one to attack
                        }
                        
                        const customFormation = `${def}-${mid}-${fwd}`;
                        bestWhiteFormation = customFormation;
                        bestColourFormation = customFormation;
                        console.log(`Created custom formation: ${customFormation} for ${correctOutfieldPlayerCount} outfield players`);
                    }
                    
                    // Update the formation states
                    setWhiteFormation(bestWhiteFormation);
                    setColourFormation(bestColourFormation);
                    
                    // Recalculate places to fill
                    const updatedWhitePlacesToFill = getPlayersNeededForFormation(bestWhiteFormation);
                    const updatedColourPlacesToFill = getPlayersNeededForFormation(bestColourFormation);
                    
                    console.log(`FIXED: Using ${bestWhiteFormation}/${bestColourFormation} formations which need ${updatedWhitePlacesToFill}/${updatedColourPlacesToFill} players`);
                } else {
                    // No formations defined for this player count, create a balanced formation
                    console.log(`No formation definitions found for ${correctOutfieldPlayerCount} outfield players. Creating custom formation...`);
                    
                    // For 6 players, use 2-2-2 as fallback
                    if (playersPerSide === 6 && !usesDedicatedKeeper) {
                        bestWhiteFormation = "2-2-2";
                        bestColourFormation = "2-2-2";
                    } 
                    // For 5 outfield + 1 keeper, use 2-1-2 as fallback
                    else if (playersPerSide === 6 && usesDedicatedKeeper) {
                        bestWhiteFormation = "2-1-2";
                        bestColourFormation = "2-1-2";
                    }
                    // For other cases, try to distribute players evenly
                    else {
                        const playerCountPerPosition = Math.floor(playersPerSide / 3);
                        const remainder = playersPerSide % 3;
                        
                        let def = playerCountPerPosition;
                        let mid = playerCountPerPosition;
                        let fwd = playerCountPerPosition;
                        
                        // Distribute remainder
                        if (remainder === 1) {
                            mid += 1; // Add extra to midfield
                        } else if (remainder === 2) {
                            def += 1; // Add one to defense
                            fwd += 1; // Add one to attack
                        }
                        
                        const fallbackFormation = `${def}-${mid}-${fwd}`;
                        console.log(`Created fallback formation: ${fallbackFormation} for ${playersPerSide} players`);
                        bestWhiteFormation = fallbackFormation;
                        bestColourFormation = fallbackFormation;
                    }
                    
                    // Update the formation states
                    setWhiteFormation(bestWhiteFormation);
                    setColourFormation(bestColourFormation);
                    
                    // Recalculate places to fill
                    const updatedWhitePlacesToFill = getPlayersNeededForFormation(bestWhiteFormation);
                    const updatedColourPlacesToFill = getPlayersNeededForFormation(bestColourFormation);
                    
                    console.log(`FIXED: Using custom ${bestWhiteFormation}/${bestColourFormation} formations which need ${updatedWhitePlacesToFill}/${updatedColourPlacesToFill} players`);
                }
            }
            
            // After ensuring formations are correct, determine how many players to assign
            const correctedWhitePlacesToFill = getPlayersNeededForFormation(bestWhiteFormation);
            const correctedColourPlacesToFill = needsTwoTeams ? getPlayersNeededForFormation(bestColourFormation) : 0;
            
            // Calculate total players needed with corrected formations
            const correctedTotalPlayersNeeded = correctedWhitePlacesToFill + correctedColourPlacesToFill;
            
            // Determine if we have enough players
            if (playersBySkill.length < correctedTotalPlayersNeeded) {
                console.log(`Warning: Not enough players to fill formations. Need ${correctedTotalPlayersNeeded}, have ${playersBySkill.length}`);
            }
            
            // Limit players for sorting to exactly the number needed for the formations
            const playersToAssign = playersBySkill.slice(0, correctedTotalPlayersNeeded);
            
            // Keep the rest as substitutes in the player pool
            const substitutes = playersBySkill.slice(correctedTotalPlayersNeeded);
            console.log(`Keeping ${substitutes.length} players as substitutes in the player pool`);
            
            // Use the snake draft method to create balanced teams
            const team1 = [];
            const team2 = [];
            
            if (!needsTwoTeams) {
                // For academy and squad formats (needsTwoTeams=false), only use one team
                console.log("Academy/Squad format: All players go to the white team");
                team1.push(...playersToAssign);
            } else {
                // For friendly matches, snake draft to create balanced teams
                playersToAssign.forEach((player, index) => {
                    // Snake draft: 0,2,4... to team1, 1,3,5... to team2, then reverse
                    const isEvenRound = Math.floor(index / 2) % 2 === 0;
                    
                    if (isEvenRound) {
                        // In even rounds: 0->team1, 1->team2
                if (index % 2 === 0) {
                            team1.push(player);
                } else {
                            team2.push(player);
                        }
                    } else {
                        // In odd rounds: 0->team2, 1->team1 (snake reversal)
                        if (index % 2 === 0) {
                            team2.push(player);
                        } else {
                            team1.push(player);
                        }
                    }
                });
            }
            
            console.log("Team 1 (White):", team1);
            console.log("Team 2 (Colour):", team2);
            
            // Step 3: Formation selection
            await new Promise(resolve => setTimeout(resolve, 1000));
            setAnalysisStep(3);
            setAnalysisMessage("Analyzing optimal player positions...");
            
            // Assign positions based on formations
            const assignedWhitePlayers = assignPlayersToFormation(team1, bestWhiteFormation);
            
            // Only assign colour players if using two teams
            const assignedColourPlayers = needsTwoTeams 
                ? assignPlayersToFormation(team2, bestColourFormation) 
                : [];
            
            console.log("Assigned White Players:", assignedWhitePlayers);
            if (needsTwoTeams) {
                console.log("Assigned Colour Players:", assignedColourPlayers);
            }
            
            // Step 4: Final team assignment
            await new Promise(resolve => setTimeout(resolve, 1000));
            setAnalysisStep(4);
            setAnalysisMessage("Finalizing team selection...");
            
            // Save the team assignments in state
            setWhitePlayers(assignedWhitePlayers.map(player => ({ ...player, team: 'white' })));
            if (needsTwoTeams) {
                setColourPlayers(assignedColourPlayers.map(player => ({ ...player, team: 'colour' })));
            }
            
            // Keep substitutes in the player pool
            setPlayerPool(substitutes);
            
            setHasLoadedPositions(true);
            
            // Final delay before completing
            await new Promise(resolve => setTimeout(resolve, 1000));
            
            // Save to the database if we have a valid match ID
            try {
                const matchId = getMatchId();
                
                if (matchId) {
                    console.log(`Saving formations for match ${matchId}`);
                    
                    // Save the formations
                    await saveFormation(matchId, 'white', bestWhiteFormation);
                    if (needsTwoTeams) {
                        await saveFormation(matchId, 'colour', bestColourFormation);
                    }
                    
                    // Save player positions
                    const allAssignedPlayers = [
                        ...assignedWhitePlayers.map(p => ({ ...p, team_color: 'white' })),
                        ...assignedColourPlayers.map(p => ({ ...p, team_color: 'colour' }))
                    ];
                    await savePlayerPositions(matchId, allAssignedPlayers);
                    
                    console.log("Successfully saved team assignments to database");
                } else {
                    console.log("No match ID found, skipping database save");
                }
        } catch (error) {
                console.error("Error saving team assignments:", error);
            }
            
        } catch (error) {
            console.error("Error in sortPlayersIntoTeams:", error);
        } finally {
            setIsLoading(false);
            setIsSorting(false);
        }
    };

    // Add comprehensive debug function to analyze team and formation selection
    const generateTeamDebugReport = () => {
        // Get match details
        const matchDetails = {
            match_id: match?.match_id || 'Not available',
            team_size: match?.team_size || 'Not specified',
            max_players: match?.max_players || 'Not specified',
            team_type: match?.team_type || 'Not specified',
        };

        // Analyze player distribution
        const playerCounts = {
            total: playerPool.length + whitePlayers.length + colourPlayers.length,
            whitePlayers: whitePlayers.length,
            colourPlayers: colourPlayers.length,
            poolPlayers: playerPool.length
        };

        // Formation analysis
        const formationDetails = {
            whiteFormation,
            colourFormation,
            whitePlayersNeeded: getPlayersNeededForFormation(whiteFormation),
            colourPlayersNeeded: getPlayersNeededForFormation(colourFormation),
        };

        // Check permanent keeper setting
        const permanentKeeper = sessionStorage.getItem('permanent_keeper');
        const keeperMode = permanentKeeper !== null && permanentKeeper !== "0" ? 'Enabled' : 'Disabled';

        // Get team type
        const teamTypeDetail = selectedTeamType || sessionStorage.getItem('selected_team_type') || 'Not specified';
        const requiresTwoTeams = !['academy', 'squad'].includes(teamTypeDetail);

        // Analyze formation selection logic
        const teamSizeFormat = match?.team_size;
        const outfieldPlayersPerTeam = getTeamSizeFormat(teamSizeFormat);
        const cleanFormat = teamSizeFormat ? teamSizeFormat.replace(/\s+/g, '') : 'None';
        
        // Analyze available formations
        const totalPlayers = playerPool.length + whitePlayers.length + colourPlayers.length;
        const availableFormationsInfo = getAvailableFormations(totalPlayers);
        
        // Calculate what should have been chosen
        const expectedPlayersPerTeam = Math.ceil(totalPlayers / 2);
        const suitableFormationIndex = permanentKeeper !== null && permanentKeeper !== "0" 
            ? expectedPlayersPerTeam - 1  // Subtract 1 for goalkeeper
            : expectedPlayersPerTeam;
            
        const suitableFormations = formations[suitableFormationIndex] || 
            `No formations found for ${suitableFormationIndex} outfield players`;

        return {
            matchDetails,
            playerCounts,
            formationDetails,
            settingsInfo: {
                permanentKeeper: keeperMode,
                teamType: teamTypeDetail,
                requiresTwoTeams,
                teamSizeFormat,
                outfieldPlayersPerTeam,
                cleanFormat
            },
            formationAnalysis: {
                availableFormations: availableFormationsInfo,
                expectedPlayersPerTeam,
                suitableFormationIndex,
                suitableFormations
            }
        };
    };

    // Toggle debug panel visibility
    const toggleDebugPanel = () => {
        const report = generateTeamDebugReport();
        setDebugInfo(report);
        setShowDebugPanel(!showDebugPanel && authService.isSuperAdmin());
        
        // Log the report to console for easy access
        console.log("TEAM BUILDER DEBUG REPORT:", report);
        
        // Advanced analysis specifically for the 12 players issue
        if (report.playerCounts.total === 12) {
            console.log("DIAGNOSING 12 PLAYER ISSUE:");
            console.log(`- Expected: 6v6 teams`);
            
            // Check for specific issues that could cause 6-a-side with 12 players
            const possibleIssues = [];
            
            if (report.settingsInfo.teamSizeFormat) {
                possibleIssues.push(`Match team_size is explicitly set to "${report.settingsInfo.teamSizeFormat}"`);
            }
            
            if (report.settingsInfo.permanentKeeper === "Enabled") {
                possibleIssues.push("Using permanent keeper mode: subtracts 1 player per team from outfield positions");
            }
            
            // Check team type influence
            if (!report.settingsInfo.requiresTwoTeams) {
                possibleIssues.push(`Team type "${report.settingsInfo.teamType}" is configured for a single team, not splitting into two balanced teams`);
            }
            
            // Check formation selection logic
            if (formationPositions[report.formationDetails.whiteFormation]) {
                const formationParts = report.formationDetails.whiteFormation.split('-').map(Number);
                const positionsInFormation = formationParts.reduce((acc, val) => acc + val, 0);
                
                if (report.settingsInfo.permanentKeeper === "Enabled") {
                    possibleIssues.push(`Formation "${report.formationDetails.whiteFormation}" has ${positionsInFormation} outfield positions + 1 goalkeeper = ${positionsInFormation + 1} total positions`);
                } else {
                    possibleIssues.push(`Formation "${report.formationDetails.whiteFormation}" has ${positionsInFormation} total positions (no dedicated goalkeeper)`);
                }
            }
            
            console.log("POTENTIAL CAUSES:");
            possibleIssues.forEach((issue, index) => {
                console.log(`${index + 1}. ${issue}`);
            });
        }
    };

    // Add a simple debug panel component 
    const DebugPanel = () => {
        if (!debugInfo) return null;
        
        return (
            <div className="debug-panel">
                <h3>Team Builder Debug Analysis</h3>
                <button onClick={toggleDebugPanel} className="close-debug-btn">Close</button>
                
                <div className="debug-section">
                    <h4>Match Configuration</h4>
                    <ul>
                        <li><strong>Match ID:</strong> {debugInfo.matchDetails.match_id}</li>
                        <li><strong>Team Size Format:</strong> {debugInfo.matchDetails.team_size}</li>
                        <li><strong>Max Players:</strong> {debugInfo.matchDetails.max_players}</li>
                        <li><strong>Team Type:</strong> {debugInfo.matchDetails.team_type}</li>
                    </ul>
                </div>
                
                <div className="debug-section">
                    <h4>Player Distribution</h4>
                    <ul>
                        <li><strong>Total Players:</strong> {debugInfo.playerCounts.total}</li>
                        <li><strong>White Team:</strong> {debugInfo.playerCounts.whitePlayers}</li>
                        <li><strong>Colour Team:</strong> {debugInfo.playerCounts.colourPlayers}</li>
                        <li><strong>Player Pool:</strong> {debugInfo.playerCounts.poolPlayers}</li>
                    </ul>
                </div>
                
                <div className="debug-section">
                    <h4>Formation Details</h4>
                    <ul>
                        <li><strong>White Formation:</strong> {debugInfo.formationDetails.whiteFormation} (needs {debugInfo.formationDetails.whitePlayersNeeded} players)</li>
                        <li><strong>Colour Formation:</strong> {debugInfo.formationDetails.colourFormation} (needs {debugInfo.formationDetails.colourPlayersNeeded} players)</li>
                    </ul>
                </div>
                
                <div className="debug-section">
                    <h4>Settings</h4>
                    <ul>
                        <li><strong>Permanent Keeper:</strong> {debugInfo.settingsInfo.permanentKeeper}</li>
                        <li><strong>Team Type:</strong> {debugInfo.settingsInfo.teamType}</li>
                        <li><strong>Requires Two Teams:</strong> {debugInfo.settingsInfo.requiresTwoTeams ? 'Yes' : 'No'}</li>
                        <li><strong>Team Size Format:</strong> {debugInfo.settingsInfo.teamSizeFormat}</li>
                        <li><strong>Outfield Players Per Team:</strong> {debugInfo.settingsInfo.outfieldPlayersPerTeam}</li>
                    </ul>
                </div>
                
                <div className="debug-section">
                    <h4>Formation Analysis</h4>
                    <div><strong>Expected Players Per Team:</strong> {debugInfo.formationAnalysis.expectedPlayersPerTeam}</div>
                    <div><strong>Suitable Formation Index:</strong> {debugInfo.formationAnalysis.suitableFormationIndex}</div>
                    <div>
                        <strong>Suitable Formations:</strong> 
                        {Array.isArray(debugInfo.formationAnalysis.suitableFormations) 
                            ? debugInfo.formationAnalysis.suitableFormations.join(', ') 
                            : debugInfo.formationAnalysis.suitableFormations}
                    </div>
                </div>
                
                {debugInfo.playerCounts.total === 12 && (
                    <div className="debug-section debug-alert">
                        <h4>12 Player Issue Analysis</h4>
                        <p>With 12 total players, we should be using 6v6 teams.</p>
                        <p>Current setup is using {debugInfo.playerCounts.whitePlayers}v{debugInfo.playerCounts.colourPlayers} 
                           with formations {debugInfo.formationDetails.whiteFormation}/{debugInfo.formationDetails.colourFormation}</p>
                        
                        <div className="debug-recommendations">
                            <h5>Recommendations:</h5>
                            <ol>
                                <li>Check if team_size is explicitly set in the match configuration</li>
                                <li>Verify permanent_keeper setting in sessionStorage</li>
                                <li>Confirm team_type is set correctly for two balanced teams</li>
                                <li>Review availableFormations calculation logic</li>
                            </ol>
                        </div>
                    </div>
                )}
            </div>
        );
    };
    
    // Update the SortingAnimation component to include a title and progress bar
    const SortingAnimation = () => {
        // Get step title based on current step
        const getStepTitle = () => {
            switch(analysisStep) {
                case 1:
                    return "Step 1 - Researching Players";
                case 2:
                    return "Step 2 - Building Balanced Teams";
                case 3:
                    return "Step 3 - Analysing Formation Options";
                case 4:
                    return "Step 4 - Finalising Team Selection";
                default:
                    return "Analysing...";
            }
        };
        
        return (
            <div className="sorting-animation-overlay">
                <div className="sorting-animation-content">
                    <h2 className="analysis-title">{getStepTitle()}</h2>
                    
                    <div className="progress-container">
                        <div className="progress-bar">
                            <div 
                                className="progress-fill" 
                                style={{ width: `${(analysisStep / totalSteps) * 100}%` }}
                            ></div>
                        </div>
                        <div className="progress-steps">
                            {Array.from({ length: totalSteps }).map((_, i) => (
                                <div 
                                    key={i} 
                                    className={`progress-step ${i + 1 <= analysisStep ? 'active' : ''}`}
                                >
                                    {i + 1}
                                </div>
                            ))}
                        </div>
                    </div>
                    
                    <div className="spinner-container">
                        <div className="spinner"></div>
                    </div>
                    <div className="analysis-message">{analysisMessage}</div>
                </div>
            </div>
        );
    };

    // Update the resetTeams function
    const resetTeams = () => {
        const matchId = getMatchId();
        if (!matchId) {
            console.error('Cannot reset: match_id is not available');
            return;
        }

        // Move all team players back to the pool
        const allTeamPlayers = [...whitePlayers, ...colourPlayers];
        setPlayerPool(prev => [...prev, ...allTeamPlayers]);
        
        // Clear all team assignments
        setWhitePlayers([]);
        setColourPlayers([]);
        // Reset formations to default
        setWhiteFormation('2-3-1');
        setColourFormation('2-3-1');
        // Allow sorting again
        setHasLoadedPositions(false);
        
        // Save the reset state to the database
        try {
            Promise.all([
                // Clear formations
                saveFormation(matchId, 'white', '2-3-1'),
                saveFormation(matchId, 'colour', '2-3-1'),
                // Delete all player positions using a special flag
                api.delete(`${process.env.REACT_APP_API_URL}/match_player_positions.php?match_id=${matchId}`)
            ]).then(() => {
                console.log('Reset successful');
            }).catch(error => {
                console.error('Error during reset:', error);
            });
        } catch (error) {
            console.error('Error saving reset state:', error);
        }
    };

    // Add a helper function to check if teams are empty
    const areTeamsEmpty = () => {
        return whitePlayers.length === 0 && colourPlayers.length === 0;
    };

    // Add state for position filter
    const [positionFilter, setPositionFilter] = React.useState('ALL');

    // Update the assignPlayerToTeam function to include the clicked position
    const assignPlayerToTeam = (player, team) => {
        // Check if player is already in any team
        const playerName = player.name;
        const playerId = player.id || player.player_id;
        
        // Check for player in teams by ID or name
        const isInWhiteTeam = whitePlayers.some(p => 
            (p.id == playerId) || (p.name && p.name.toLowerCase() === playerName.toLowerCase())
        );
        const isInColourTeam = colourPlayers.some(p => 
            (p.id == playerId) || (p.name && p.name.toLowerCase() === playerName.toLowerCase())
        );

        if (isInWhiteTeam || isInColourTeam) {
            // Player is already assigned, don't allow reassignment
            console.warn(`Player ${player.name} (ID: ${playerId}) already assigned to a team`);
            return;
        }

        // Remove from pool (ensure we're using the correct ID)
        console.log(`Removing player from pool via assignPlayerToTeam: ${player.name} (ID: ${playerId})`);
        
        setPlayerPool(prev => {
            const newPool = prev.filter(p => {
                const id = p.id || p.player_id;
                const name = p.name;
                
                // Match by ID or name (if IDs don't match)
                const shouldRemoveById = id == playerId;
                const shouldRemoveByName = !shouldRemoveById && name && 
                    name.toLowerCase() === playerName.toLowerCase();
                const shouldKeep = !(shouldRemoveById || shouldRemoveByName);
                
                if (!shouldKeep) {
                    console.log(`assignPlayerToTeam matched player to remove: ${p.name} (ID: ${id}), matched by: ${shouldRemoveById ? 'ID' : 'name'}`);
                }
                return shouldKeep;
            });
            
            console.log(`Pool size before: ${prev.length}, after: ${newPool.length}`);
            return newPool;
        });
        
        // Add position from selectedPosition if it exists
        const playerWithTeam = {
            ...player,
            team,
            position: selectedPosition ? selectedPosition.position : 'midfield-center'
        };

        // Add to appropriate team
        if (team === 'white') {
            setWhitePlayers(prev => [...prev, playerWithTeam]);
        } else {
            setColourPlayers(prev => [...prev, playerWithTeam]);
        }

        // Clear selected position after assignment
        setSelectedPosition(null);
    };

    // Filter players based on position

    // Add helper function to count players by position
    const getPositionCount = (position) => {
        // Filter out players already assigned to a team
        const availablePlayers = playerPool.filter(player => {
            const playerId = player.id || player.player_id;
            const playerName = player.name;
            
            // Check if player is in white team by ID or name
            const isInWhiteTeam = whitePlayers.some(p => 
                (p.id == playerId) || 
                (p.name && playerName && p.name.toLowerCase() === playerName.toLowerCase())
            );
            
            // Check if player is in colour team by ID or name
            const isInColourTeam = colourPlayers.some(p => 
                (p.id == playerId) || 
                (p.name && playerName && p.name.toLowerCase() === playerName.toLowerCase())
            );
            
            return !isInWhiteTeam && !isInColourTeam;
        });
        
        if (position === 'ALL') return availablePlayers.length;
        return availablePlayers.filter(player => player.position_db === position).length;
    };

    // Add helper function to get player status indicator
    const getPlayerStatusIcon = (player) => {
        if (parseInt(player.form_rating) >= 8) {
            return <span title="Exceptional Form" className="status-icon">🔥</span>;
        }
        if (player.recent_motm > 0) {
            return <span title="Recent MOTM" className="status-icon">👑</span>;
        }
        return null;
    };

    // Add calculateTeamScore function
    const calculateTeamScore = (players) => {
        if (!players.length) return 0;

        // Calculate scores for each position group
        const attackScore = parseFloat(calculatePositionGroupStats(players, 'forward').overall) || 0;
        const midfieldScore = parseFloat(calculatePositionGroupStats(players, 'midfield').overall) || 0;
        const defenseScore = parseFloat(calculatePositionGroupStats(players, 'defense').overall) || 0;

        // Weight the scores (can adjust weights as needed)
        const weightedScore = (
            (attackScore * 0.35) +
            (midfieldScore * 0.35) +
            (defenseScore * 0.3)
        );

        return weightedScore.toFixed(1);
    };

    // Add function to handle player swaps
    const handleSwapPlayers = (player1, player2) => {
        // Check if player2 is from the player pool (unassigned)
        if (!player2.team) {
            // Handle moving player from team to pool, and pool to team
            
            // Add player2 (from pool) to the team that player1 was in
            const player2WithTeam = {
                ...player2,
                team: player1.team,
                position: player1.position
            };
            
            // Update appropriate team based on which team player1 was in
            if (player1.team === 'white') {
                setWhitePlayers(prev => prev.map(p => 
                    p.id === player1.id ? null : p
                ).filter(Boolean).concat(player2WithTeam));
            } else {
                setColourPlayers(prev => prev.map(p => 
                    p.id === player1.id ? null : p
                ).filter(Boolean).concat(player2WithTeam));
            }
            
            // Move player1 to the pool
            setPlayerPool(prev => {
                // First remove player2 from the pool
                const newPool = prev.filter(p => p.id !== player2.id);
                // Then add player1 to the pool
                return [...newPool, {...player1, team: undefined, position: player1.position_db === 'GK' ? 'defense-center' : 'midfield-center'}];
            });
            
            return;
        }
        
        // Regular swap between two team players
        const allPlayers = [...whitePlayers, ...colourPlayers];
        
        // Create new array with swapped positions and teams
        const updatedPlayers = allPlayers.map(p => {
            if (p.id === player1.id) {
                return { ...p, position: player2.position, team: player2.team };
            }
            if (p.id === player2.id) {
                return { ...p, position: player1.position, team: player1.team };
            }
            return p;
        });
        
        // Split back into teams
        const newWhitePlayers = updatedPlayers.filter(p => p.team === 'white');
        const newColourPlayers = updatedPlayers.filter(p => p.team === 'colour');
        
        // Update state in a single batch to ensure synchronous update
        React.startTransition(() => {
            setWhitePlayers(newWhitePlayers);
            setColourPlayers(newColourPlayers);
        });
    };

    // Update useEffect to only initialize playerPool when playerDetails prop changes
    React.useEffect(() => {
        if (playerDetails?.length > 0) {
            setPlayerPool(prev => {
                if (prev.length === 0) {
                    return playerDetails.map(player => ({
                        ...player,
                        position: player.position_db === 'GK' ? 'defense-center' : 'midfield-center'
                    }));
                }
                return prev;
            });
        }
    }, [playerDetails]);

    // Add new empty positions for a formation
    const getEmptyPositions = (formation) => {
        const formationParts = formation.split('-').map(Number);
        
        const positions = [];
        
        // Special handling for 4-2-3-1 formation
        if (formation === '4-2-3-1') {
            // Forward - single striker
            positions.push({ position: 'forward-center', top: 20, left: 50 });
            
            // Midfield - 3 attacking midfielders
            positions.push({ position: 'midfield-left', top: 35, left: 25 });
            positions.push({ position: 'midfield-center', top: 35, left: 50 });
            positions.push({ position: 'midfield-right', top: 35, left: 75 });
            
            // Midfield - 2 defensive midfielders
            positions.push({ position: 'midfield-center-left', top: 50, left: 35 });
            positions.push({ position: 'midfield-center-right', top: 50, left: 65 });
            
            // Defense - back four
            positions.push({ position: 'defense-left', top: 65, left: 20 });
            positions.push({ position: 'defense-center-left', top: 65, left: 40 });
            positions.push({ position: 'defense-center-right', top: 65, left: 60 });
            positions.push({ position: 'defense-right', top: 65, left: 80 });
            
            return positions;
        }
        
        // Handle different formation structures for other formations
        let def, mid, fwd;
        
        if (formationParts.length === 4) {
            // Other 4-part formations
            def = formationParts[0];
            mid = formationParts[1] + formationParts[2]; // Combine the two midfield layers
            fwd = formationParts[3];
        } else {
            // Regular 3-part formation
            [def, mid, fwd] = formationParts;
        }

        // Forward positions
        if (fwd === 1) {
            positions.push({ position: 'forward-center', top: 25, left: 50 });
        } else if (fwd === 2) {
            positions.push({ position: 'forward-left', top: 25, left: 30 });
            positions.push({ position: 'forward-right', top: 25, left: 70 });
        } else if (fwd === 3) {
            positions.push({ position: 'forward-left', top: 25, left: 25 });
            positions.push({ position: 'forward-center', top: 25, left: 50 });
            positions.push({ position: 'forward-right', top: 25, left: 75 });
        } else if (fwd === 4) {
            positions.push({ position: 'forward-left', top: 25, left: 25 });
            positions.push({ position: 'forward-center-left', top: 25, left: 42 });
            positions.push({ position: 'forward-center-right', top: 25, left: 58 });
            positions.push({ position: 'forward-right', top: 25, left: 75 });
        }

        // Midfield positions
        if (mid === 1) {
            positions.push({ position: 'midfield-center', top: 45, left: 50 });
        } else if (mid === 2) {
            positions.push({ position: 'midfield-left', top: 45, left: 30 });
            positions.push({ position: 'midfield-right', top: 45, left: 70 });
        } else if (mid === 3) {
            positions.push({ position: 'midfield-left', top: 45, left: 25 });
            positions.push({ position: 'midfield-center', top: 45, left: 50 });
            positions.push({ position: 'midfield-right', top: 45, left: 75 });
        } else if (mid === 4) {
            positions.push({ position: 'midfield-left', top: 45, left: 25 });
            positions.push({ position: 'midfield-center-left', top: 45, left: 42 });
            positions.push({ position: 'midfield-center-right', top: 45, left: 58 });
            positions.push({ position: 'midfield-right', top: 45, left: 75 });
        } else if (mid === 5) {
            // For formations like 4-2-3-1 (which we handle as 4-5-1)
            positions.push({ position: 'midfield-left', top: 45, left: 20 });
            positions.push({ position: 'midfield-center-left', top: 45, left: 35 });
            positions.push({ position: 'midfield-center', top: 45, left: 50 });
            positions.push({ position: 'midfield-center-right', top: 45, left: 65 });
            positions.push({ position: 'midfield-right', top: 45, left: 80 });
        }

        // Defense positions
        if (def === 1) {
            positions.push({ position: 'defense-center', top: 65, left: 50 });
        } else if (def === 2) {
            positions.push({ position: 'defense-left', top: 65, left: 30 });
            positions.push({ position: 'defense-right', top: 65, left: 70 });
        } else if (def === 3) {
            positions.push({ position: 'defense-left', top: 65, left: 25 });
            positions.push({ position: 'defense-center', top: 65, left: 50 });
            positions.push({ position: 'defense-right', top: 65, left: 75 });
        } else if (def === 4) {
            positions.push({ position: 'defense-left', top: 65, left: 25 });
            positions.push({ position: 'defense-center-left', top: 65, left: 42 });
            positions.push({ position: 'defense-center-right', top: 65, left: 58 });
            positions.push({ position: 'defense-right', top: 65, left: 75 });
        } else if (def === 5) {
            positions.push({ position: 'defense-left', top: 65, left: 15 });
            positions.push({ position: 'defense-center-left', top: 65, left: 30 });
            positions.push({ position: 'defense-center', top: 65, left: 50 });
            positions.push({ position: 'defense-center-right', top: 65, left: 70 });
            positions.push({ position: 'defense-right', top: 65, left: 85 });
        }

        return positions;
    };

    // Add new state for selected position
    // const [selectedPosition, setSelectedPosition] = React.useState(null);

    const findOptimalFormation = (players) => {
        if (players.length === 0) return null;
        
        // Get team size based on match data
        const teamSizeFormat = match?.team_size;
        let preferredOutfieldPlayers = getTeamSizeFormat(teamSizeFormat);
        
        // Special handling for 11v11 matches
        if (teamSizeFormat && teamSizeFormat.replace(/\s+/g, '').toLowerCase() === '11v11') {
            preferredOutfieldPlayers = 10; // 11v11 format means 10 outfield players
            console.log(`Setting preferred outfield players to 10 for 11v11 format`);
        }
        
        console.log(`Finding optimal formation for ${players.length} players with preferred outfield players: ${preferredOutfieldPlayers}`);
        
        // Get all available formations for this player count
        let availableFormations = getAvailableFormations(players.length);
        
        // For academy/squad matches with team_size defined, filter to only allow correct formations
        if (preferredOutfieldPlayers) {
            // For 11v11 format, use the key 10 in formations object
            if (preferredOutfieldPlayers === 10 && formations[10]) {
                // Restrict to 11v11 formations
                availableFormations = formations[10];
                console.log(`Restricted formations to 11v11 format (10 outfield players):`, availableFormations);
            } 
            // For other formats, use the normal lookup
            else if (formations[preferredOutfieldPlayers]) {
                // Restrict to formations for this team size
                availableFormations = formations[preferredOutfieldPlayers];
                console.log(`Restricted formations to match team size ${teamSizeFormat}:`, availableFormations);
            }
        }
        
        if (!availableFormations || availableFormations.length === 0) {
            console.warn('No available formations found for', players.length, 'players');
            return null;
        }
        
        // Use the first formation as a fallback
        let bestFormation = availableFormations[0];
        let bestScore = 0;
        
        // Evaluate each formation
        availableFormations.forEach(formation => {
            const score = evaluateFormation(players, formation);
            
            if (score > bestScore) {
                bestScore = score;
                bestFormation = formation;
            }
        });

        return bestFormation;
    };

    // Add helper function to reset teams
    const resetTeamsToPool = () => {
        // Combine all players into pool
        const allPlayers = [...whitePlayers, ...colourPlayers];
        setPlayerPool(prev => [...prev, ...allPlayers]);
        setWhitePlayers([]);
        setColourPlayers([]);
    };

    // First, remove the shuffleTeams function completely
    const shuffleTeams = () => {
        // First reset all players to pool
        const allPlayers = [...playerPool, ...whitePlayers, ...colourPlayers];
        
        if (allPlayers.length === 0) return;

        // Reset current teams
        setWhitePlayers([]);
        setColourPlayers([]);
        setPlayerPool([]);

        // Sort players by skill rating and position
        const sortedPlayers = [...allPlayers].sort((a, b) => {
            // First by skill rating
            const ratingDiff = (parseInt(b.skill_rating) || 0) - (parseInt(a.skill_rating) || 0);
            if (ratingDiff !== 0) return ratingDiff;
            
            // Then by form rating
            return (parseInt(b.form_rating) || 0) - (parseInt(a.form_rating) || 0);
        });

        // Create balanced teams
        const teams = [[], []];
        let teamIndex = 0;

        // Distribute players alternately, starting with highest rated
        sortedPlayers.forEach(player => {
            teams[teamIndex].push({
                ...player,
                team: teamIndex === 0 ? 'white' : 'colour'
            });
            teamIndex = (teamIndex + 1) % 2;
        });
        
        // Get formations based on actual player count
        const teamFormations = getAvailableFormations(sortedPlayers.length);

        // Find optimal formations for each team
        const whiteOptimalFormation = findBestFormation(teams[0], teamFormations);
        const colourOptimalFormation = findBestFormation(teams[1], teamFormations);

        // Assign positions based on optimal formations
        const assignedWhite = assignPlayersToFormation(teams[0], whiteOptimalFormation);
        const assignedColour = assignPlayersToFormation(teams[1], colourOptimalFormation);

        // Update all state in a specific order
        setTimeout(() => {
            setWhiteFormation(whiteOptimalFormation);
            setColourFormation(colourOptimalFormation);
            setWhitePlayers(assignedWhite);
            setColourPlayers(assignedColour);
        }, 0);
    };

    // Add this new function to calculate formation effectiveness
    const calculateFormationEffectiveness = (players, formation) => {
        if (!formation) return { score: 0, forward: 0, midfield: 0, defense: 0 };
        
        const formationParts = formation.split('-').map(Number);
        let def, mid, fwd;
        
        if (formationParts.length === 4) {
            // Special handling for 4-part formations like 4-2-3-1
            def = formationParts[0];
            mid = formationParts[1] + formationParts[2];
            fwd = formationParts[3];
        } else {
            // Regular 3-part formation
            [def, mid, fwd] = formationParts;
        }
        
        // Helper to calculate position effectiveness
        const calculatePositionEffectiveness = (player, positionType) => {
            // Base stats for each position
            const stats = {
                'forward': (parseFloat(player.shooting) + parseFloat(player.pace)) / 2,
                'midfield': (parseFloat(player.passing) + parseFloat(player.dribbling)) / 2,
                'defense': (parseFloat(player.defending) + parseFloat(player.physical)) / 2
            };

            // Position suitability multiplier
            const positionMultipliers = {
                'forward': {
                    'FWD': 1.0,
                    'MID': 0.7,
                    'DEF': 0.4,
                    'GK': 0.3
                },
                'midfield': {
                    'FWD': 0.7,
                    'MID': 1.0,
                    'DEF': 0.7,
                    'GK': 0.4
                },
                'defense': {
                    'FWD': 0.4,
                    'MID': 0.7,
                    'DEF': 1.0,
                    'GK': 0.9
                }
            };

            const multiplier = positionMultipliers[positionType][player.position_db] || 0.5;
            return stats[positionType] * multiplier;
        };

        // Calculate raw stats for each position group
        const rawStats = {
            forward: players
                .filter(p => p.position.includes('forward'))
                .reduce((acc, player) => {
                    return acc + calculatePositionEffectiveness(player, 'forward');
                }, 0) * Math.min(fwd / 2, 1),
            midfield: players
                .filter(p => p.position.includes('midfield'))
                .reduce((acc, player) => {
                    return acc + calculatePositionEffectiveness(player, 'midfield');
                }, 0) * Math.min(mid / 3, 1),
            defense: players
                .filter(p => p.position.includes('defense'))
                .reduce((acc, player) => {
                    return acc + calculatePositionEffectiveness(player, 'defense');
                }, 0) * Math.min(def / 3, 1)
        };

        // Normalize stats to 0-100 range
        // Assuming max possible stat is 150
        const normalizedStats = {
            forward: Math.min(100, (rawStats.forward / 150) * 100),
            midfield: Math.min(100, (rawStats.midfield / 150) * 100),
            defense: Math.min(100, (rawStats.defense / 150) * 100)
        };

        return {
            score: Math.round((normalizedStats.forward + normalizedStats.midfield + normalizedStats.defense) / 3),
            stats: normalizedStats,
            strengths: [],
            weaknesses: []
        };
    };

    // Add touch handling for swipe gestures
    const FormationSummary = ({ players, currentFormation, allFormations, playerDetails, whitePlayers, colourPlayers }) => {
        const [touchStart, setTouchStart] = React.useState(null);
        const [touchEnd, setTouchEnd] = React.useState(null);
        
        // Use a safe version of allFormations with a fallback based on team size
        const totalPlayers = whitePlayers.length + colourPlayers.length;
        
        // Get the actual number of available players in the match
        const totalAvailablePlayers = playerPool.length + whitePlayers.length + colourPlayers.length;
        
        // Check if we're in academy/squad mode (single team) or friendly mode (two teams)
        const isAcademyOrSquad = !needsTwoTeams;
        
        // Calculate players per team depending on mode
        let actualPlayersPerTeam;
        if (isAcademyOrSquad) {
            // For academy/squad, all players go to a single team
            actualPlayersPerTeam = totalAvailablePlayers;
            
            // If a team_size is specified in the match, use that
            if (match?.team_size) {
                const parts = match.team_size.replace(/\s+/g, '').toLowerCase().split('v');
                if (parts.length === 2) {
                    // Get the number before 'v' as players per team
                    const specifiedPlayersPerTeam = parseInt(parts[0]);
                    if (!isNaN(specifiedPlayersPerTeam)) {
                        actualPlayersPerTeam = specifiedPlayersPerTeam;
                    }
                }
            }
        } else {
            // For friendly mode, players are split between two teams
            actualPlayersPerTeam = Math.floor(totalAvailablePlayers / 2);
        }
        
        // Check if we're using permanent keeper
        const permanentKeeperSetting = sessionStorage.getItem('permanent_keeper');
        const usesDedicatedKeeper = permanentKeeperSetting !== null && permanentKeeperSetting !== "0";
        
        // Calculate correct formation index based on actual player count
        // For formations.js, the index is always one less than the number of players
        // Because the formations are indexed by outfield players
        const correctFormationIndex = usesDedicatedKeeper ? actualPlayersPerTeam - 2 : actualPlayersPerTeam - 1;
        
        console.log(`FormationSummary: Total available players: ${totalAvailablePlayers}, actual players per team: ${actualPlayersPerTeam}`);
        console.log(`FormationSummary: Team mode: ${isAcademyOrSquad ? 'Academy/Squad (single team)' : 'Friendly (two teams)'}`);
        console.log(`FormationSummary: Using formation index: ${correctFormationIndex} (keeper mode: ${usesDedicatedKeeper ? 'enabled' : 'disabled'})`);
        
        // First, get all available formations for the current formation index
        let safeFormations = [];
        
        if (allFormations?.length) {
            safeFormations = allFormations;
            console.log(`FormationSummary: Using provided allFormations:`, allFormations);
        } else if (formations[correctFormationIndex]) {
            // Use formations that match the actual player count
            safeFormations = formations[correctFormationIndex];
            console.log(`FormationSummary: Using formations for ${correctFormationIndex} outfield players:`, safeFormations);
        } else if (defaultFormations[correctFormationIndex]) {
            // Fall back to default formations for the correct player count
            safeFormations = defaultFormations[correctFormationIndex];
            console.log(`FormationSummary: Using default formations for ${correctFormationIndex} outfield players:`, safeFormations);
        } else {
            // Last resort fallback
            safeFormations = defaultFormations.default;
            console.log(`FormationSummary: Using default fallback formations:`, safeFormations);
        }
        
        // DEBUGGING: Store original formations before filtering
        // Include both the correct formations and fallbacks if needed
        let allPossibleFormations = [];
        
        // First add the correct formations for this player count
        if (formations[correctFormationIndex]) {
            allPossibleFormations = [...formations[correctFormationIndex]];
        }
        
        // Special handling for 8-player teams with no goalkeeper (need 7-a-side formations)
        if (actualPlayersPerTeam === 8 && !usesDedicatedKeeper) {
            console.log("Special case: 8 players per team with no keeper, using 7-a-side formations");
            // Ensure we're using formations for 7 outfield players (index 7)
            if (formations[7]) {
                safeFormations = [...formations[7]];
                allPossibleFormations = [...formations[7]];
                console.log("Using 7-a-side formations:", safeFormations);
            }
        }
        
        console.log(`FormationSummary: Formation index ${correctFormationIndex} has ${safeFormations.length} formations`);
        
        // Check if these formations are correct for the player count
        safeFormations.forEach(formation => {
            const formationParts = formation.split('-').map(Number);
            const outfieldPlayers = formationParts.reduce((sum, count) => sum + count, 0);
            console.log(`Formation ${formation} requires ${outfieldPlayers} outfield players`);
        });
        
        // Filter formations to only include those that match the actual player count
        const filteredFormations = safeFormations.filter(formation => {
            const formationParts = formation.split('-').map(Number);
            const outfieldPlayers = formationParts.reduce((sum, count) => sum + count, 0);
            
            if (usesDedicatedKeeper) {
                // With keeper mode enabled, we need formations where outfield players = actualPlayersPerTeam - 1
                return outfieldPlayers === (actualPlayersPerTeam - 1);
            } else {
                // With keeper mode disabled, we need formations where outfield players = actualPlayersPerTeam
                return outfieldPlayers === actualPlayersPerTeam;
            }
        });
        
        // If we have filtered formations, use them, otherwise keep all formations for more options
        if (filteredFormations.length > 0) {
            console.log(`FormationSummary: Found ${filteredFormations.length} formations that match ${actualPlayersPerTeam} players exactly.`);
            safeFormations = filteredFormations;
        } else {
            console.log(`FormationSummary: No exact matches found. Creating custom formation for ${actualPlayersPerTeam} players.`);
            
            // Create a balanced formation based on the actual player count
            const outfieldPlayersToUse = usesDedicatedKeeper ? actualPlayersPerTeam - 1 : actualPlayersPerTeam;
            
            // Special case for 8-player teams without keeper
            if (actualPlayersPerTeam === 8 && !usesDedicatedKeeper) {
                safeFormations = ['3-2-3', '2-3-3', '3-3-2', '2-4-2'];
                console.log(`Created special formations for 8 players without keeper:`, safeFormations);
            } else {
                const playerCountPerPosition = Math.floor(outfieldPlayersToUse / 3);
                const remainder = outfieldPlayersToUse % 3;
                
                let def = playerCountPerPosition;
                let mid = playerCountPerPosition;
                let fwd = playerCountPerPosition;
                
                // Distribute remainder
                if (remainder === 1) {
                    mid += 1; // Add extra to midfield
                } else if (remainder === 2) {
                    def += 1; // Add one to defense
                    fwd += 1; // Add one to attack
                }
                
                const customFormation = `${def}-${mid}-${fwd}`;
                safeFormations = [customFormation];
                console.log(`Created custom formation: ${customFormation} for ${outfieldPlayersToUse} outfield players (${actualPlayersPerTeam} total players)`);
            }
        }
        
        // Add currentIndex calculation
        const currentIndex = safeFormations.indexOf(currentFormation);
        
        // If current formation isn't in the safe list, use the first formation
        if (currentIndex === -1 && safeFormations.length > 0) {
            console.log(`FormationSummary: Current formation ${currentFormation} not in safe list. Using ${safeFormations[0]} instead.`);
            if (activeTeam === 'white') {
                handleWhiteFormationChange({ target: { value: safeFormations[0] } });
            } else {
                handleColourFormationChange({ target: { value: safeFormations[0] } });
            }
        }
        
        const minSwipeDistance = 50;

        const onTouchStart = (e) => {
            setTouchEnd(null);
            setTouchStart(e.touches[0].clientX);
        };

        const onTouchMove = (e) => {
            setTouchEnd(e.touches[0].clientX);
        };

        const onTouchEnd = () => {
            if (!touchStart || !touchEnd) return;
            
            const distance = touchStart - touchEnd;
            const isLeftSwipe = distance > minSwipeDistance;
            const isRightSwipe = distance < -minSwipeDistance;
            
            if (isLeftSwipe) {
                handleNextFormation();
            }
            if (isRightSwipe) {
                handlePrevFormation();
            }
        };

        // Calculate scores for all formations once
        const formationScores = React.useMemo(() => {
            // First calculate raw scores without stability bonus
            const rawScores = safeFormations.map(formation => {
                const effectiveness = calculateFormationEffectiveness(players, formation);
                return {
                    formation,
                    rawScore: effectiveness.score,  // Keep track of raw score
                    ...effectiveness
                };
            }).sort((a, b) => b.score - a.score);
            
            // Get the best formation before stability bonus
            const bestFormation = rawScores[0].formation;
            const bestScore = rawScores[0].rawScore;  // Store the best raw score
            
            // Then add stability bonus and resort
            return rawScores.map(score => {
                const stabilityBonus = score.formation === currentFormation ? 3 : 0;
                const newScore = score.score + stabilityBonus;
                return {
                    ...score,
                    score: newScore,
                    isActualBest: score.rawScore === bestScore  // Compare raw scores instead
                };
            }).sort((a, b) => b.score - a.score);
        }, [players, safeFormations, currentFormation]);

        const bestFormation = formationScores[0];
        const currentStats = formationScores.find(f => f.formation === currentFormation) || bestFormation;
        const isRecommended = currentStats?.rawScore === formationScores[0].rawScore;

        // Helper function to safely get stat values
        const getStatValue = (stat, stats) => {
            return parseFloat(stats?.[stat]) || 0;
        };

        const handlePrevFormation = () => {
            const newIndex = currentIndex > 0 ? currentIndex - 1 : safeFormations.length - 1;
            const newFormation = safeFormations[newIndex];
            if (activeTeam === 'white') {
                handleWhiteFormationChange({ target: { value: newFormation } });
            } else {
                handleColourFormationChange({ target: { value: newFormation } });
            }
        };

        const handleNextFormation = () => {
            const newIndex = currentIndex < safeFormations.length - 1 ? currentIndex + 1 : 0;
            const newFormation = safeFormations[newIndex];
            if (activeTeam === 'white') {
                handleWhiteFormationChange({ target: { value: newFormation } });
            } else {
                handleColourFormationChange({ target: { value: newFormation } });
            }
        };

        return (
            <div className="formation-summary">
                <div className="formation-effectiveness">
                    <div className="formation-nav">
                        {!isMatchFinished() && (
                            <>
                                <button className="nav-btn prev" onClick={handlePrevFormation}>
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
                                        <path fill="none" stroke="currentColor" strokeWidth="2" d="M15 6l-6 6 6 6"/>
                                    </svg>
                                </button>
                                <div className="current-formation">
                                    <span className="formation-label">Formation</span>
                                    <span className="formation-value">{currentFormation}</span>
                                </div>
                                <button className="nav-btn next" onClick={handleNextFormation}>
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
                                        <path fill="none" stroke="currentColor" strokeWidth="2" d="M9 6l6 6-6 6"/>
                                    </svg>
                                </button>
                            </>
                        )}
                        {isMatchFinished() && (
                            <div className="current-formation">
                                <span className="formation-label">Formation</span>
                                <span className="formation-value">{currentFormation}</span>
                            </div>
                        )}
                    </div>
                    
                    {/* Add debug display for all available formations - only for Super Admin */}
                    { !isMatchFinished() && authService.isSuperAdmin() && (
                    <div className="formation-debug">
                        <div className="debug-title">Available Formations:</div>
                        <div className="available-formations">
                            {safeFormations.map((formation, index) => (
                                <div key={index} className={`formation-item ${formation === currentFormation ? 'active' : ''}`}>
                                    {formation}
                                </div>
                            ))}
                        </div>
                        
                        <div className="debug-title">All Possible Formations:</div>
                        <div className="available-formations">
                            {allPossibleFormations.map((formation, index) => (
                                <div 
                                    key={`all-${index}`} 
                                    className={`formation-item ${formation === currentFormation ? 'active' : ''} ${safeFormations.includes(formation) ? 'recommended' : 'alternate'}`}
                                    onClick={() => {
                                        if (activeTeam === 'white') {
                                            handleWhiteFormationChange({ target: { value: formation } });
                                        } else {
                                            handleColourFormationChange({ target: { value: formation } });
                                        }
                                    }}
                                >
                                    {formation}
                                </div>
                            ))}
                        </div>
                        
                        <div className="debug-info">
                            <div>Team Mode: {isAcademyOrSquad ? 'Academy/Squad (1 team)' : 'Friendly (2 teams)'}</div>
                            <div>Total Players: {totalAvailablePlayers}</div>
                            <div>Players Per Team: {actualPlayersPerTeam}</div>
                            <div>Outfield Players: {usesDedicatedKeeper ? actualPlayersPerTeam - 1 : actualPlayersPerTeam}</div>
                            <div>Formation Index: {correctFormationIndex}</div>
                            <div>Current Formation: {currentFormation}</div>
                            <div>Keeper Mode: {usesDedicatedKeeper ? 'Enabled' : 'Disabled'}</div>
                            
                            <div className="keeper-settings">
                                <button 
                                    className={`keeper-toggle ${usesDedicatedKeeper ? 'enabled' : 'disabled'}`}
                                    onClick={() => {
                                        if (usesDedicatedKeeper) {
                                            sessionStorage.setItem('permanent_keeper', '0');
                                        } else {
                                            sessionStorage.setItem('permanent_keeper', '1');
                                        }
                                        window.location.reload();
                                    }}
                                >
                                    {usesDedicatedKeeper ? 'Disable Keeper' : 'Enable Keeper'}
                                </button>
                            </div>
                        </div>
                    </div>
                    )}
                </div>
                <div className="formation-stats">
                    {['forward', 'midfield', 'defense'].map(stat => {
                        const bestValue = getStatValue(stat, bestFormation.stats);
                        const currentValue = getStatValue(stat, currentStats.stats);
                        
                        return (
                            <div key={stat} className="stat">
                                <span className="label">
                                    {stat.charAt(0).toUpperCase() + stat.slice(1)}
                                </span>
                                <div className="stat-bar">
                                    {/* Recommended formation's stats shown at their actual value */}
                                    <div 
                                        className="stat-fill recommended"
                                        style={{width: `${(bestValue / 150) * 100}%`}}
                                    />
                                    {/* Current formation's stats shown at their actual value */}
                                    <div 
                                        className="stat-fill current"
                                        style={{width: `${(currentValue / 150) * 100}%`}}
                                    />
                                </div>
                                <span className="stat-value">{Math.round(currentValue)}</span>
                            </div>
                        );
                    })}
                </div>
                {!isMatchFinished() && (
                    <div className="formation-explanation">
                        <div className="title">How AI Creates Balanced Teams</div>
                        <p>
                            Our AI system analyses player form, recent performance, and positional data to build optimally balanced teams.
                            Forward ratings prioritise shooting and pace, Midfield focuses on passing and dribbling, 
                            while Defense emphasises defending and physical attributes. The algorithm evaluates player chemistry
                            and distributes talent to ensure competitive matches.
                        </p>
                    </div>
                )}

                {isAdmin && (
                    <>
                    {!isMatchFinished() && (
                        <>
                        <div className="formation-explanation">
                            <div className="title">Player Form Information</div>
                        </div>
                        
                        <div className="player-form-container">
                            <div className="player-form-scroll">
                                {playerDetails && playerDetails.length > 0 ? (
                                    playerDetails.map((player, index) => {
                                        // Function to render form badge
                                        const renderFormBadge = (formRating) => {
                                            let color = '#777';
                                            switch(formRating) {
                                                case 'Excellent': color = '#4CAF50'; break;
                                                case 'Good': color = '#2196F3'; break;
                                                case 'Average': color = '#FF9800'; break;
                                                case 'Rusty': color = '#F44336'; break;
                                                default: color = '#777';
                                            }
                                            
                                            return (
                                                <span style={{
                                                    display: 'inline-block',
                                                    padding: '2px 6px',
                                                    borderRadius: '3px',
                                                    fontSize: '12px',
                                                    fontWeight: 'bold',
                                                    color: 'white',
                                                    backgroundColor: color
                                                }}>
                                                    {formRating}
                                                </span>
                                            );
                                        };
                                        
                                        // Format the date
                                        const formattedDate = player.last_played_date 
                                            ? format(new Date(player.last_played_date), 'EEE do MMM yyyy')
                                            : 'Never';
                                        
                                        // Check which team the player is on (if any)
                                        const isOnWhiteTeam = whitePlayers.some(p => p.id === player.id);
                                        const isOnColourTeam = colourPlayers.some(p => p.id === player.id);
                                        
                                        // Also check if the player has a team directly associated with them
                                        // This data comes from the match_player_positions API
                                        const isWhiteTeamFromPlayer = player.team === 'white';
                                        const isColourTeamFromPlayer = player.team === 'colour';
                                        
                                        // First check if player is in our UI state arrays
                                        // Then check if player has direct team assignment in their data
                                        const isWhite = isOnWhiteTeam || isWhiteTeamFromPlayer;
                                        const isColour = isOnColourTeam || isColourTeamFromPlayer;
                                        
                                        const teamIndicator = isWhite ? teamWhiteName : 
                                                             isColour ? teamColourName : 'Not Assigned';
                                        const teamColor = isWhite ? '#f8f9fa' : 
                                                         isColour ? '#bd1313' : '#777';
                                        
                                        // Console log for debugging
                                        console.log(`Player ${player.name} team assignment:`, { 
                                            id: player.id,
                                            isOnWhiteTeam, 
                                            isOnColourTeam,
                                            isWhiteTeamFromPlayer,
                                            isColourTeamFromPlayer,
                                            team: player.team,
                                            teamIndicator
                                        });
                                        
                                        return (
                                            <div key={index} className="player-form-card" onClick={isMatchFinished() ? undefined : () => setSelectedPlayer(player)}>
                                                <div className="player-card-header">
                                                    {/* Remove the team badge div that appears above the name */}
                                                    <div className="player-name-container">
                                                        <div className="shirt-number">{player.shirt_number || player.number || '?'}</div>
                                                        <div className="player-name">{player.name}</div>
                                                    </div>
                                                    <div className="form-section">
                                                        <div className="form-rating">
                                                            <span className="form-label">Form:</span>
                                                            <span className={`form-value ${(player.form_rating || 'unknown').toLowerCase()}`}>
                                                                {player.form_rating || 'Unknown'}
                                                            </span>
                                                        </div>
                                                        <div className="assigned-team">
                                                            <span className="team-label">Team:</span>
                                                            <span className={`team-value ${teamIndicator.toLowerCase()}`}>
                                                                {teamIndicator}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                                
                                                <div className="player-form-description">
                                                    {player.form_description || `Won ${player.recent_wins || 0} of last ${player.matches_played_90days || 0} matches`}
                                                </div>
                                                
                                                <div className="player-stats-container">
                                                    <div className="last-played" style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                        <span className="played-label">Last played:</span>
                                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                                            <span className="played-value">Mon 31 Jan, 2025</span>
                                                            <span className="played-ago">(6 days ago)</span>
                                                        </div>
                                                    </div>
                                                    
                                                    <div className="player-stat">
                                                        <span className="stat-label">Avg Goals/Game:</span>
                                                        <span>{calculateAverageGoalsPerGame(player)}</span>
                                                    </div>
                                                    
                                                    <div className="player-stat">
                                                        <span className="stat-label">xG:</span>
                                                        <span>{calculatePlayerXG(player)}</span>
                                                    </div>
                                                    
                                                    <div className="player-stat">
                                                        <span className="stat-label">Win rate:</span>
                                                        <span>{player.win_rate || 0}%</span>
                                                    </div>
                                                </div>
                                                
                                                <div className="player-recent-results">
                                                    <div className="section-label">Recent Results</div>
                                                    <div className="results-circles">
                                                        {(player.match_results || []).map((result, idx) => {
                                                            // Check if result is an object instead of a string
                                                            let resultDisplay = result;
                                                            let bgColor = '#9e9e9e'; // Default color
                                                            let matchDate = 'Unknown date';
                                                            
                                                            if (typeof result === 'object' && result !== null) {
                                                                // Handle the case where result is an object
                                                                resultDisplay = result.result || '?';
                                                                
                                                                // Extract the match date if available
                                                                if (result.match_date) {
                                                                    try {
                                                                        matchDate = format(new Date(result.match_date), 'EEE do MMM yyyy');
                                                                    } catch (e) {
                                                                        matchDate = result.match_date || 'Unknown date';
                                                                    }
                                                                }
                                                                
                                                                // Extract the result value from the object
                                                                if (resultDisplay === 'win') {
                                                                    resultDisplay = 'W';
                                                                    bgColor = '#4caf50';
                                                                } else if (resultDisplay === 'loss') {
                                                                    resultDisplay = 'L';
                                                                    bgColor = '#f44336';
                                                                } else if (resultDisplay === 'draw') {
                                                                    resultDisplay = 'D';
                                                                    bgColor = '#ff9800';
                                                                } else if (resultDisplay === 'upcoming') {
                                                                    resultDisplay = 'U';
                                                                }
                                                            } else {
                                                                // Handle the case where result is already a string
                                                                bgColor = 
                                                                    result === 'W' ? '#4caf50' : 
                                                                    result === 'L' ? '#f44336' : 
                                                                    result === 'D' ? '#ff9800' : 
                                                                    result === 'U' ? '#9e9e9e' : '#9e9e9e';
                                                            }
                                                            
                                                            return (
                                                                <div 
                                                                    key={idx} 
                                                                    className="result-circle" 
                                                                    style={{ backgroundColor: bgColor }}
                                                                    title={`Result: ${resultDisplay === 'W' ? 'Win' : 
                                                                                    resultDisplay === 'L' ? 'Loss' : 
                                                                                    resultDisplay === 'D' ? 'Draw' : 
                                                                                    resultDisplay === 'U' ? 'Upcoming' : 
                                                                                    resultDisplay} - ${matchDate}`}
                                                                >
                                                                    {resultDisplay}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })
                                ) : (
                                    <div className="no-players-message">
                                        <div className="message-text">
                                            No player information available
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        </>
                    )}
                    </>
                )}  
            </div>
        );
    };

    const PlayerCard = ({ player, playerDetails, whitePlayers, colourPlayers, onClose, onSwapPlayers }) => {
        const [selectedComparePlayer, setSelectedComparePlayer] = React.useState(null);

        // Helper function to format position display
        const formatPosition = (positionDb) => {
            switch(positionDb) {
                case 'DEF': return 'Defence';
                case 'MID': return 'Midfield';
                case 'FWD': return 'Attack';
                default: return positionDb;
            }
        };

        // Helper function to get stats for a player
        const getPlayerStats = (p) => ({
            PAC: p.pace || 0,
            SHO: p.shooting || 0,
            PAS: p.passing || 0,
            DRI: p.dribbling || 0,
            DEF: p.defending || 0,
            PHY: p.physical || 0
        });

        // Calculate points for hexagon
        const calculatePoint = (value, index) => {
            const angle = (Math.PI * 2 * index) / 6;
            const radius = (value / 100) * 80; // 80 is the radius of our chart
            return {
                x: 100 + radius * Math.sin(angle),
                y: 100 - radius * Math.cos(angle)
            };
        };

        // Get points string for comparison player
        const getPointsStringForPlayer = (comparePlayer) => {
            const compareStats = getPlayerStats(comparePlayer);
            const comparePoints = Object.values(compareStats)
                .map((value, index) => calculatePoint(value, index));
            return comparePoints.map(p => `${p.x},${p.y}`).join(' ');
        };

        // Generate player summary based on stats
        const generatePlayerSummary = (stats) => {
            const highestStat = Object.entries(stats).reduce((a, b) => stats[a] > stats[b] ? a : b)[0];
            const lowestStat = Object.entries(stats).reduce((a, b) => stats[a] < stats[b] ? a : b)[0];
            
            const statNames = {
                PAC: 'pace',
                SHO: 'shooting',
                PAS: 'passing',
                DRI: 'dribbling',
                DEF: 'defending',
                PHY: 'physical'
            };
            
            return `A ${player.position_db.toLowerCase()} player with exceptional ${statNames[highestStat]} abilities. ` +
                `While their ${statNames[lowestStat]} could be improved, they excel in ${statNames[highestStat]} situations.`;
        };

        const stats = getPlayerStats(player);

        const playerSummary = generatePlayerSummary(stats);

        const points = Object.values(stats).map((value, index) => calculatePoint(value, index));
        const pointsString = points.map(p => `${p.x},${p.y}`).join(' ');

        const renderComparisonButtons = () => {
            if (!selectedComparePlayer) return null;
            
            return (
                <>
                    <div className="comparison-header">
                        {player.name} <span className="vs">vs</span> {selectedComparePlayer.name}
                    </div>
                    <div className="comparison-buttons">
                        <button 
                            className="swap-button"
                            onClick={() => {
                                onSwapPlayers(player, selectedComparePlayer);
                                onClose();
                            }}
                        >
                            Swap Players
                        </button>
                        <button 
                            className="cancel-button"
                            onClick={() => setSelectedComparePlayer(null)}
                        >
                            Cancel
                        </button>
                    </div>
                </>
            );
        };
                                    
        return (
            <div className="modal-overlay" onClick={onClose}>
                <div className="modal-content player-card" onClick={e => e.stopPropagation()}>
                    <button className="close-button" onClick={onClose}>×</button>
                    
                    <div className="player-header">
                        <h3>{player.name}</h3>
                        <div className="player-info">
                            <div className="player-info-row">
                                <span className="info-label">Position: </span>
                                <div className="player-position">{formatPosition(player.position_db)}</div>
                            </div>
                            <div className="player-info-row">
                                <span className="info-label">Foot: </span>
                                <div className="player-foot">
                                    {player.preferred_foot ? (
                                        <span className="value">{player.preferred_foot}</span>
                                    ) : 'BOTH'}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="player-summary">
                        {playerSummary}
                    </div>

                    <div className="stats-radar">
                        <svg className="radar-chart" viewBox="0 0 200 200">
                            {/* Background hexagon */}
                            <polygon 
                                className="background-hexagon"
                                points="100,20 180,60 180,140 100,180 20,140 20,60"
                            />
                            
                            {/* Stats polygon */}
                            <polygon 
                                className="stats-polygon"
                                points={pointsString}
                            />
                            
                            {/* Comparison polygon */}
                            {selectedComparePlayer && (
                                <polygon 
                                    className="comparison-polygon"
                                    points={getPointsStringForPlayer(selectedComparePlayer)}
                                />
                            )}
                            
                            {/* Stat points */}
                            {points.map((point, index) => (
                                <circle
                                    key={index}
                                    className="stat-point"
                                    cx={point.x}
                                    cy={point.y}
                                    r="3"
                                />
                            ))}
                            
                            {/* Stat labels */}
                            {Object.keys(stats).map((stat, index) => {
                                const labelPoint = calculatePoint(100, index);
                                return (
                                    <text
                                        key={stat}
                                        className="stat-label"
                                        x={labelPoint.x}
                                        y={labelPoint.y}
                                        data-position={stat}
                                        textAnchor="middle"
                                    >
                                        {stat}
                                    </text>
                                );
                            })}
                        </svg>
                    </div>

                    {renderComparisonButtons()}

                    <div className="swap-section">
                        <div className="section-title">Swap Position With:</div>
                        <div className="swap-options">
                            {[...whitePlayers, ...colourPlayers, ...playerPool]
                                .filter((p, index, self) => {
                                    if (p.id === player.id) return false;
                                    return index === self.findIndex(t => t.id === p.id);
                                })
                                .map(otherPlayer => (
                                    <div
                                        key={otherPlayer.id}
                                        className={`player-option ${selectedComparePlayer?.id === otherPlayer.id ? 'selected' : ''} ${!otherPlayer.team ? 'unassigned' : ''}`}
                                        onClick={() => setSelectedComparePlayer(otherPlayer)}
                                    >
                                        <div className="player-name">{otherPlayer.name}</div>
                                        <div className="player-position">
                                            {formatPosition(otherPlayer.position_db)}
                                            {!otherPlayer.team && <span className="team-status"> (Unassigned)</span>}
                                        </div>
                                    </div>
                                ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Extract match ID from URL
    const getMatchId = () => {
        const pathParts = window.location.pathname.split('/');
        const matchIdFromUrl = pathParts[pathParts.indexOf('match') + 1];
        return match?.id || parseInt(matchIdFromUrl) || null;
    };

    // Add validation check for match prop
    React.useEffect(() => {
        const matchId = getMatchId();
        if (!matchId) {
            console.error('Match data is missing:', match);
        }
    }, [match]);

    // Add these functions to handle saving formations and positions
    const saveFormation = async (matchId, teamColor, formation) => {
        if (!matchId) {
            throw new Error('match_id is required but was not provided');
        }
        console.log('Saving formation with data:', {
            match_id: matchId,
            team_color: teamColor,
            formation: formation
        });

        try {
            // Create URLSearchParams object
            const params = new URLSearchParams();
            params.append('match_id', matchId);
            params.append('team_color', teamColor);
            params.append('formation', formation);

            const response = await api.post(
                `${process.env.REACT_APP_API_URL}/match_formations.php`, 
                params,
                {
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                    }
                }
            );
            
            const data = response.data;
            console.log('Formation save response:', data);
            
            if (data.error) {
                throw new Error(data.error || 'Failed to save formation');
            }
            return true;
        } catch (error) {
            console.error('Error saving formation:', error);
            throw error;
        }
    };

    const savePlayerPositions = async (matchId, players) => {
        // Validate input
        if (!matchId) {
            throw new Error('match_id is required but was not provided');
        }
        if (!Array.isArray(players)) {
            throw new Error('players must be an array');
        }
        if (players.length === 0) {
            console.log('No players to save, skipping save operation');
            return true;
        }

        console.log('Saving player positions with data:', {
            match_id: matchId,
            players: players
        });

        try {
            const positions = players.map(player => {
                const playerId = player.player_id || player.id;
                
                if (!playerId) {
                    console.error('Player missing id:', player);
                    throw new Error('Player is missing id');
                }

                // Ensure position is a string and truncate to 20 characters max to prevent SQL error
                // The error occurs because the position column likely has a VARCHAR(20) constraint
                let position = player.position || '';
                if (typeof position !== 'string') {
                    position = String(position);
                }
                if (position.length > 20) {
                    console.warn(`Position value too long (${position.length} chars), truncating to 20 chars: ${position}`);
                    position = position.substring(0, 20);
                }

                return {
                    match_id: parseInt(matchId),
                    player_id: parseInt(playerId),
                    team_color: player.team,
                    position: position
                };
            });

            console.log('Sending positions data:', positions);

            // Send positions array directly
            const response = await api.post(
                `${process.env.REACT_APP_API_URL}/match_player_positions.php`,
                positions,  // Send array directly, not wrapped in an object
                {
                    headers: {
                        'Content-Type': 'application/json',
                    }
                }
            );
            
            const data = response.data;
            console.log('Player positions save response:', data);
            
            if (data.error) {
                throw new Error(data.error || 'Failed to save player positions');
            }
            return true;
        } catch (error) {
            console.error('Error saving player positions:', error);
            throw error;
        }
    };

    // Add function to handle player team color updates
    const updatePlayerTeamColor = async (playerId, teamColor) => {
        try {
            const response = await api.patch(`${process.env.REACT_APP_API_URL}/invited/${match.id}/player/${playerId}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ team_color: teamColor })
            });
            if (!response.ok) throw new Error('Failed to update player team');
        } catch (error) {
            console.error('Error updating player team:', error);
        }
    };

    // Add this function to check for dropouts
    const checkForDropouts = async (currentPlayers) => {
        const assignedPlayers = [...whitePlayers, ...colourPlayers];
        
        try {
            const droppedPlayers = assignedPlayers.filter(assigned => {
                const assignedId = assigned.id || assigned.player_id;
                const stillPlaying = currentPlayers.some(current => 
                    (current.player_id === assignedId || current.id === assignedId) && 
                    current.response === 'Yes'
                );
                return !stillPlaying;
            });
            const dropoutCount = droppedPlayers.length;

            if (dropoutCount > 0) {
                setDropouts([
                    'A player has dropped out. Update the teams before announcing'
                ]);
            } else {
                setDropouts([]); // Clear dropouts if no players have dropped out
            }
        } catch (error) {
            console.error('Error checking for dropouts:', error);
        }
    };

    // Modify the useEffect to check for dropouts more frequently
    React.useEffect(() => {
        const loadFormationsAndPositions = async () => {
            try {
                setIsLoading(true);
                const matchId = getMatchId();
                
                // Add debug logging for match data
                if (match) {
                    console.log('Match data loaded:', {
                        match_id: match.match_id,
                        team_size: match.team_size,
                        team_type: match.team_type,
                        max_players: match.max_players
                    });
                }
                
                // Fetch current attendance
                const attendanceResponse = await api.get(`${process.env.REACT_APP_API_URL}/get_attendance.php?match_id=${matchId}`);
                const attendanceData = attendanceResponse.data;
                const yesPlayers = attendanceData.success ? (attendanceData.data.yes || []) : [];
    
                // Load player positions
                const positionsResponse = await api.get(`${process.env.REACT_APP_API_URL}/match_player_positions.php?match_id=${matchId}`);
                const positions = positionsResponse.data;
                
                if (Array.isArray(positions) && positions.length > 0 && playerDetails) {
                    // If we found positions in the database, teams have been published
                    setTeamsPublished(true);
                    
                    // Merge player details with positions
                    const positionsWithDetails = positions.map(position => {
                        const playerDetail = playerDetails.find(p => p?.id === position?.player_id);
                        if (!playerDetail) {
                            console.warn(`No player details found for player_id: ${position?.player_id}`);
                            return null;
                        }
                        return {
                            ...playerDetail,
                            ...position,
                            team: position.team_color
                        };
                    }).filter(Boolean); // Remove any null entries
    
                    // Split players into teams
                    const whiteTeam = positionsWithDetails.filter(p => p.team_color === 'white');
                    const colourTeam = positionsWithDetails.filter(p => p.team_color === 'colour');
                    
                    // Update teams
                    setWhitePlayers(whiteTeam);
                    setColourPlayers(colourTeam);
                    
                    // Create a list of player IDs that are already assigned to teams
                    const assignedPlayers = positionsWithDetails.map(player => {
                        const id = player.id || player.player_id;
                        console.log(`Player assigned to team: ${player.name}, ID: ${id}`);
                        return {
                            id: id,
                            name: player.name
                        };
                    });
                    
                    // Remove assigned players from the playerPool
                    setPlayerPool(prev => {
                        const filtered = prev.filter(player => {
                            const playerId = player.id || player.player_id;
                            const playerName = player.name;
                            
                            // Check if player is assigned by ID
                            const isAssignedById = assignedPlayers.some(p => p.id == playerId);
                            
                            // If not found by ID, check by name as fallback (case insensitive)
                            const isAssignedByName = !isAssignedById && 
                                assignedPlayers.some(p => 
                                    p.name.toLowerCase() === playerName.toLowerCase()
                                );
                            
                            const isAssigned = isAssignedById || isAssignedByName;
                            
                            // Debug any ID matching issues
                            if (isAssigned) {
                                console.log(`Removing from pool: ${player.name}, ID: ${playerId} (matched by ${isAssignedById ? 'ID' : 'name'})`);
                            }
                            
                            return !isAssigned;
                        });
                        
                        console.log(`PlayerPool before: ${prev.length}, after: ${filtered.length}`);
                        return filtered;
                    });
                    
                    // Use the checkForDropouts function to check for dropouts
                    await checkForDropouts(yesPlayers);
                    
                    setHasLoadedPositions(true);
                }
    
                // Load formations
                const formationsResponse = await api.get(`${process.env.REACT_APP_API_URL}/match_formations.php?match_id=${matchId}`);
                const formations = formationsResponse.data;
                
                if (Array.isArray(formations?.formations)) {
                    formations.formations.forEach(f => {
                        if (f.team_color === 'white') setWhiteFormation(f.formation);
                        if (f.team_color === 'colour') setColourFormation(f.formation);
                    });
                }
            } catch (error) {
                console.error('Error loading formations and positions:', error);
            } finally {
                setIsLoading(false);
            }
        };
    
        if (getMatchId()) {
            loadFormationsAndPositions();
        }
    }, [match, playerDetails]);

    // Add the notification component
    const renderDropoutNotification = () => {
        if (dropouts.length === 0) return null;
                                    
        return (
            <div className="dropout-notification">
                <div className="notification-content">
                    <span className="warning-icon">⚠️</span>
                    <div className="message">
                        <strong>Team Update Required</strong>
                        <p>{dropouts.join('\n')}</p>
                    </div>
                </div>
            </div>
        );
    };

    // Update the captureScreenshot function to show team name and match details only in the preview
    const captureScreenshot = async () => {
        try {
            // Get the entire football pitch element to include the background
            const footballPitch = document.querySelector('.football-pitch');
            if (!footballPitch) {
                console.error('Football pitch element not found');
                alert('Error: Football pitch element not found');
                return;
            }
            
            // Show loading indicator
            console.log('Starting screenshot capture...');
            
            // Create a debug message
            const debugDiv = document.createElement('div');
            debugDiv.style.position = 'fixed';
            debugDiv.style.bottom = '10px';
            debugDiv.style.left = '10px';
            debugDiv.style.backgroundColor = 'rgba(0,0,0,0.8)';
            debugDiv.style.color = 'white';
            debugDiv.style.padding = '10px';
            debugDiv.style.zIndex = '9999';
            debugDiv.style.borderRadius = '5px';
            debugDiv.textContent = 'Capturing screenshot...';
            document.body.appendChild(debugDiv);
            
            // Hide the screenshot button temporarily
            const screenshotButton = document.querySelector('.screenshot-button');
            let originalButtonDisplay = null;
            if (screenshotButton) {
                originalButtonDisplay = screenshotButton.style.display;
                screenshotButton.style.display = 'none';
            }
            
            // Show team name and match details for the screenshot
            const teamNameDisplay = document.querySelector('.team-name-display');
            const matchDetailsDisplay = document.querySelector('.match-details-display');
            let teamNameOriginalDisplay = null;
            let matchDetailsOriginalDisplay = null;
            
            if (teamNameDisplay) {
                teamNameOriginalDisplay = teamNameDisplay.style.display;
                teamNameDisplay.style.display = 'block';
            }
            
            if (matchDetailsDisplay) {
                matchDetailsOriginalDisplay = matchDetailsDisplay.style.display;
                matchDetailsDisplay.style.display = 'block';
            }
            
            // Fix SVG rendering issues before capture
            const playerShirts = document.querySelectorAll('.player-position .team-shirt-container svg');
            const originalSvgStyles = [];
            
            // Store original styles and apply fixes
            playerShirts.forEach((svg, index) => {
                originalSvgStyles[index] = {
                    width: svg.style.width,
                    height: svg.style.height,
                    display: svg.style.display,
                    visibility: svg.style.visibility
                };
                
                // Apply fixed styles for better rendering
                svg.style.width = '100%';
                svg.style.height = 'auto';
                svg.style.display = 'block';
                //svg.style.visibility = 'hidden'; // Hide player SVGs as requested
                
                // Force SVG to redraw
                svg.setAttribute('data-capture', 'true');
            });
            
            // Fix the branding logo SVG
            const brandingLogo = document.querySelector('.branding-logo');
            let originalLogoSrc = null;
            let originalLogoStyles = null;
            
            if (brandingLogo) {
                // Save original logo properties
                originalLogoSrc = brandingLogo.src;
                originalLogoStyles = {
                    width: brandingLogo.style.width,
                    height: brandingLogo.style.height,
                    display: brandingLogo.style.display
                };
                
                // Create a canvas version of the SVG logo
                const canvas = document.createElement('canvas');
                canvas.width = 240; // Double size for better quality
                canvas.height = 80;
                const ctx = canvas.getContext('2d');
                
                // Create a temporary image to draw to canvas
                const tempImg = new Image();
                tempImg.crossOrigin = 'Anonymous';
                
                // Wait for the image to load before proceeding
                await new Promise((resolve) => {
                    tempImg.onload = () => {
                        // Draw the logo to canvas
                        ctx.drawImage(tempImg, 0, 0, canvas.width, canvas.height);
                        
                        // Replace the SVG with the canvas data URL
                        brandingLogo.src = canvas.toDataURL('image/png');
                        brandingLogo.style.width = '120px';
                        brandingLogo.style.height = '40px';
                        brandingLogo.style.display = 'block';
                        
                        resolve();
                    };
                    
                    tempImg.onerror = () => {
                        console.error('Failed to load logo for canvas conversion');
                        resolve(); // Continue even if logo fails
                    };
                    
                    tempImg.src = originalLogoSrc;
                });
            }
            
            // Take the screenshot of the entire football pitch
            const canvas = await html2canvas(footballPitch, {
                backgroundColor: '#1a2d3d',
                scale: 2, // Higher quality
                logging: true,
                allowTaint: true,
                useCORS: true,
                imageTimeout: 0, // No timeout for images
                onclone: (clonedDoc) => {
                    // Additional fixes for the cloned document
                    const clonedShirts = clonedDoc.querySelectorAll('.player-position .team-shirt-container svg');
                    clonedShirts.forEach(svg => {
                        // Ensure SVGs are visible in the clone
                        svg.style.width = '100%';
                        svg.style.height = 'auto';
                        svg.style.display = 'block';
                        //svg.style.visibility = 'hidden'; // Keep player SVGs hidden
                    });
                    
                    // Remove the screenshot button from the clone
                    const clonedButton = clonedDoc.querySelector('.screenshot-button');
                    if (clonedButton) clonedButton.remove();
                    
                    debugDiv.textContent = 'Document cloned, processing...';
                }
            });
            
            // Restore original SVG styles
            playerShirts.forEach((svg, index) => {
                svg.style.width = originalSvgStyles[index].width;
                svg.style.height = originalSvgStyles[index].height;
                svg.style.display = originalSvgStyles[index].display;
                svg.style.visibility = originalSvgStyles[index].visibility;
                
                // Remove temporary attribute
                svg.removeAttribute('data-capture');
            });
            
            // Restore the original logo
            if (brandingLogo && originalLogoSrc) {
                brandingLogo.src = originalLogoSrc;
                brandingLogo.style.width = originalLogoStyles.width;
                brandingLogo.style.height = originalLogoStyles.height;
                brandingLogo.style.display = originalLogoStyles.display;
            }
            
            // Restore the team name and match details display
            if (teamNameDisplay && teamNameOriginalDisplay !== null) {
                teamNameDisplay.style.display = teamNameOriginalDisplay;
            }
            
            if (matchDetailsDisplay && matchDetailsOriginalDisplay !== null) {
                matchDetailsDisplay.style.display = matchDetailsOriginalDisplay;
            }
            
            // Restore the screenshot button
            if (screenshotButton && originalButtonDisplay !== null) {
                screenshotButton.style.display = originalButtonDisplay;
            }
            
            debugDiv.textContent = 'Screenshot captured, preparing preview...';
            
            // Convert to data URL
            const dataUrl = canvas.toDataURL('image/png');
            console.log('Screenshot data URL length:', dataUrl.length);
            
            // Set the preview image
            setScreenshotPreview(dataUrl);
            
            // Update debug message
            debugDiv.textContent = 'Preview ready!';
            setTimeout(() => {
                document.body.removeChild(debugDiv);
            }, 2000);
            
        } catch (error) {
            console.error('Error capturing screenshot:', error);
            alert('Failed to capture screenshot: ' + error.message);
        }
    };

    // Add a simpler modal that appears at the bottom of the page
    const renderScreenshotPreview = () => {
        if (!screenshotPreview) return null;
        
        return (
            <div className="screenshot-preview-container">
                <div className="screenshot-preview-content">
                    <h3>Team Lineup Preview</h3>
                    <div className="screenshot-image">
                        <img src={screenshotPreview} alt="Team lineup" />
                    </div>
                    <div className="screenshot-actions">
                        <button onClick={() => setScreenshotPreview(null)}>Cancel</button>
                        <button onClick={downloadScreenshot}>Download</button>
                    </div>
                </div>
            </div>
        );
    };

    // Add a function to download the image
    const downloadScreenshot = () => {
        if (!screenshotPreview) {
            console.error('No screenshot to download');
            return;
        }
        
        // Create download link
        const link = document.createElement('a');
        link.href = screenshotPreview;
        link.download = `team-lineup-${activeTeam}-${new Date().toISOString().split('T')[0]}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Close the preview after download
        setScreenshotPreview(null);
    };

    // Add a check before rendering
    if (!match) {
        return <div>Loading...</div>;
    }

    // Add functions to calculate last match goals and xG
    const calculateLastMatchGoals = (player) => {
        if (!player || !player.match_history || !Array.isArray(player.match_history) || player.match_history.length === 0) {
            return 0;
        }
        
        // Sort matches by date in descending order to get the most recent first
        const sortedMatches = [...player.match_history].sort((a, b) => {
            return new Date(b.match_date) - new Date(a.match_date);
        });
        
        // Return goals from the most recent match
        return sortedMatches[0]?.goals || 0;
    };

    const calculatePlayerXG = (player) => {
        if (!player) return 0;
        
        // Calculate xG based on position and player attributes
        let baseXG = 0;
        
        // Position-based xG
        switch (player.position_db) {
            case 'FWD':
                baseXG = 0.75; // Forwards have higher base xG
                break;
            case 'MID':
                baseXG = 0.35; // Midfielders have medium base xG
                break;
            case 'DEF':
                baseXG = 0.15; // Defenders have lower base xG
                break;
            default:
                baseXG = 0.1;
        }
        
        // Adjust based on shooting and form
        const shootingFactor = parseFloat(player.shooting) / 100 || 0.5;
        
        // Form multiplier
        let formMultiplier = 1;
        switch (player.form_rating) {
            case 'Excellent': formMultiplier = 1.3; break;
            case 'Good': formMultiplier = 1.15; break;
            case 'Average': formMultiplier = 1; break;
            case 'Rusty': formMultiplier = 0.8; break;
            default: formMultiplier = 1;
        }
        
        // Recent goals impact
        const recentGoalsFactor = Math.min(1.5, 1 + (player.recent_goals * 0.1)) || 1;
        
        // Calculate final xG
        const xg = baseXG * shootingFactor * formMultiplier * recentGoalsFactor;
        
        // Round to 2 decimal places
        return Math.round(xg * 100) / 100;
    };

    // Add a function to calculate average goals per game
    const calculateAverageGoalsPerGame = (player) => {
        if (!player || !player.match_history || !Array.isArray(player.match_history) || player.match_history.length === 0) {
            return 0;
        }
        
        // Get total goals and matches
        let totalGoals = 0;
        let matchesWithData = 0;
        
        player.match_history.forEach(match => {
            if (match && typeof match.goals !== 'undefined') {
                totalGoals += parseInt(match.goals) || 0;
                matchesWithData++;
            }
        });
        
        // Calculate average
        if (matchesWithData === 0) return 0;
        
        // Round to 2 decimal places
        return Math.round((totalGoals / matchesWithData) * 100) / 100;
    };

    // Add function to handle adding player to active team
    const handleAddPlayerToTeam = (player) => {
        // For academy or squad, always add to white team
        const targetTeam = !needsTwoTeams ? 'white' : activeTeam;
        assignPlayerToTeam(player, targetTeam);
    };

    // Add position warning notification component
    const renderPositionWarning = () => {
        if (!positionWarning) return null;
        
        return (
            <div className="position-warning-overlay">
                <div className="position-warning-modal">
                    <h3>{positionWarning.title}</h3>
                    <p>{positionWarning.message}</p>
                    <button 
                        className="close-warning-button"
                        onClick={() => setPositionWarning(null)}
                    >
                        Got it
                    </button>
                </div>
            </div>
        );
    };

    return (
        <div className="team-builder" data-team-type={!needsTwoTeams ? 'squad' : 'friendly'}>
            {!isMatchFinished() && (
                <div className="player-pool">
                    <h3 className='tab-title'>{teamWhiteName} vs {teamColourName}</h3>
                    <div className="intro">
                        {isAdmin ? (
                            "Choose \"Sort Players\" to automatically create balanced teams. You can then adjust the teams as needed and save your team. Once you are happy with the team, you can publish the team and notify all players."
                        ) : (
                            "View your team and position for the upcoming match."
                        )}
                    </div>
                    
                    {isAdmin && !isMatchFinished() && (
                        <div className="team-actions">
                            <button 
                                className="action-button sort-button"
                                onClick={sortPlayersIntoTeams}
                                disabled={playerPool.length === 0 || hasLoadedPositions}
                            >
                                Sort Teams
                            </button>
                            <button 
                                className="action-button"
                                onClick={resetTeams}
                                disabled={areTeamsEmpty()}
                            >
                                Reset Teams
                            </button>
                        </div>
                    )}
                    
                    {/* Player selection carousel */}
                    {isAdmin && !isMatchFinished() && (
                        <div className="player-carousel-container">
                            <div className="carousel-header">
                                <h4>Available Players</h4>
                                <div className="team-selection-label">
                                    <span>Add to:</span>
                                    {needsTwoTeams ? (
                                    <div className="toolbar team-selector">
                                        <button 
                                            className={`team-button ${activeTeam === 'white' ? 'active' : ''}`}
                                            onClick={() => setActiveTeam('white')}
                                        >
                                            {teamWhiteName}
                                        </button>
                                        <button 
                                            className={`team-button ${activeTeam === 'colour' ? 'active' : ''}`}
                                            onClick={() => setActiveTeam('colour')}
                                        >
                                            {teamColourName}
                                        </button>
                                    </div>
                                    ) : (
                                        <div>{teamWhiteName}</div>
                                    )}
                                </div>
                            </div>
                            
                            <div className="position-filter">
                                <button 
                                    className={`filter-button ${positionFilter === 'ALL' ? 'active' : ''}`}
                                    onClick={() => setPositionFilter('ALL')}
                                >
                                    ALL <span className="count">({getPositionCount('ALL')})</span>
                                </button>
                                <button 
                                    className={`filter-button ${positionFilter === 'FWD' ? 'active' : ''}`}
                                    onClick={() => setPositionFilter('FWD')}
                                >
                                    FWD <span className="count">({getPositionCount('FWD')})</span>
                                </button>
                                <button 
                                    className={`filter-button ${positionFilter === 'MID' ? 'active' : ''}`}
                                    onClick={() => setPositionFilter('MID')}
                                >
                                    MID <span className="count">({getPositionCount('MID')})</span>
                                </button>
                                <button 
                                    className={`filter-button ${positionFilter === 'DEF' ? 'active' : ''}`}
                                    onClick={() => setPositionFilter('DEF')}
                                >
                                    DEF <span className="count">({getPositionCount('DEF')})</span>
                                </button>
                                <button 
                                    className={`filter-button ${positionFilter === 'GK' ? 'active' : ''}`}
                                    onClick={() => setPositionFilter('GK')}
                                >
                                    GK <span className="count">({getPositionCount('GK')})</span>
                                </button>
                            </div>
                            
                            {playerPool.length > 0 ? (
                                <div className="player-carousel">
                                    {playerPool
                                        .filter(player => positionFilter === 'ALL' || player.position_db === positionFilter)
                                        // Filter out players already assigned to a team
                                        .filter(player => {
                                            const playerId = player.id || player.player_id;
                                            const playerName = player.name;
                                            
                                            // Check if player is in white team by ID or name
                                            const isInWhiteTeam = whitePlayers.some(p => 
                                                (p.id == playerId) || 
                                                (p.name && playerName && p.name.toLowerCase() === playerName.toLowerCase())
                                            );
                                            
                                            // Check if player is in colour team by ID or name
                                            const isInColourTeam = colourPlayers.some(p => 
                                                (p.id == playerId) || 
                                                (p.name && playerName && p.name.toLowerCase() === playerName.toLowerCase())
                                            );
                                            
                                            return !isInWhiteTeam && !isInColourTeam;
                                        })
                                        .map(player => {
                                        const details = playerDetails?.find(p => p.id === player.id) || {};
                                        const formRating = player.form_rating || details.form_rating;
                                        
                                        return (
                                            <div key={player.id} className="player-carousel-item">
                                                <div className="player-card">
                                                    <div className="player-name">{player.name}</div>
                                                    <div className="player-info-row">
                                                        <div className="player-position">{player.position_db}</div>
                                                        {formRating && (
                                                            <div className={`form-indicator ${formRating.toLowerCase()}`}>
                                                                {formRating}
                                                            </div>
                                                        )}
                                                    </div>
                                                    {getPlayerStatusIcon(player)}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="empty-players-message">
                                    <div className="message-icon">👥</div>
                                    <p>All players have been assigned to teams</p>
                                    <button 
                                        className="reset-button"
                                        onClick={resetTeams}
                                        title="Reset all teams and return players to the pool"
                                    >
                                        Reset Teams
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {dropouts.length > 0 && (
                <div className="dropout-notification">
                    <div className="notification-content">
                        <span className="warning-icon">⚠️</span>
                        <div className="message">
                            <strong>Team Update Required</strong>
                            <p>{dropouts.join('\n')}</p>
                        </div>
                    </div>
                </div>
            )}

            <div className="football-pitch">
                <div className={`pitch-markings formation-${activeTeam === 'white' ? whiteFormation : colourFormation}`}>
                    {activeTeam === 'white' ? 
                        whitePlayers.map((player, index) => {
                            const position = getPlayerPosition(player, 'white', whiteFormation);
                            return (
                                <div 
                                    key={`white-${player.id}-${index}`}
                                    className={`player-position white ${player.position}`}
                                    style={{
                                        top: `${position.top}%`,
                                        left: `${position.left}%`
                                    }}
                                    onClick={() => !isMatchFinished() && setSelectedPlayer(player)}
                                >
                                    <PlayerShirt 
                                        lastName={player.last_name || ''}
                                        shirtNumber={player.number}
                                        color={WHITE_SHIRT_COLOUR}
                                    />
                                </div>
                            );
                        })
                        :
                        colourPlayers.map((player, index) => {
                            const position = getPlayerPosition(player, 'colour', colourFormation);
                            return (
                                <div 
                                    key={`colour-${player.id}-${index}`}
                                    className={`player-position colour ${player.position}`}
                                    style={{
                                        top: `${position.top}%`,
                                        left: `${position.left}%`
                                    }}
                                    onClick={() => !isMatchFinished() && setSelectedPlayer(player)}
                                >
                                    <PlayerShirt 
                                        lastName={player.last_name || ''}
                                        shirtNumber={player.number}
                                        color={getTeamKitColour()}
                                    />
                                </div>
                            );
                        })
                    }
                    
                    {/* Add logo and branding to bottom corner */}
                    <div className="pitch-branding">
                        <img 
                            src={logo}
                            alt="FirstWhistle"
                        />
                    </div>
                    
                    {/* Add team name to top left */}
                    <div className="team-name-display">
                        {activeTeam === 'white' ? teamWhiteName : teamColourName} Team
                    </div>
                    
                    {/* Add match details if available */}
                    {match && (
                        <div className="match-details-display">
                            {match.location} - {format(new Date(match.match_date), 'EEEE, MMMM d, yyyy')}
                        </div>
                    )}
                </div>
            </div>
            
            

            {/* Bottom team selector */}
            {needsTwoTeams ? (
            <div className="toolbar team-selector">
                    <button 
                        className={`team-button ${activeTeam === 'white' ? 'active' : ''}`}
                        onClick={() => setActiveTeam('white')}
                    >
                        {teamWhiteName}
                    </button>
                    <button 
                        className={`team-button ${activeTeam === 'colour' ? 'active' : ''}`}
                        onClick={() => setActiveTeam('colour')}
                    >
                        {teamColourName}
                    </button>
            </div>
            ) : null}

           

            {playerDetails && whitePlayers && colourPlayers && (
                <FormationSummary 
                    players={activeTeam === 'white' ? whitePlayers : colourPlayers}
                    currentFormation={activeTeam === 'white' ? whiteFormation : colourFormation}
                    allFormations={availableFormations}
                    playerDetails={playerDetails}
                    whitePlayers={whitePlayers}
                    colourPlayers={colourPlayers}
                />
            )}
            {selectedPlayer && (
                <PlayerCard 
                    player={selectedPlayer}
                    playerDetails={playerDetails.find(p => p.id === selectedPlayer.id)}
                    whitePlayers={whitePlayers}
                    colourPlayers={colourPlayers}
                    onClose={() => setSelectedPlayer(null)}
                    onSwapPlayers={handleSwapPlayers}
                />
            )}

            {selectedPosition && (
                <div className="modal-overlay" onClick={() => setSelectedPosition(null)}>
                    <div className="modal-content position-modal" onClick={e => e.stopPropagation()}>
                        <button className="close-button" onClick={() => setSelectedPosition(null)}>×</button>
                        <h3>Select Player for {selectedPosition.position.split('-')[0].toUpperCase()}</h3>
                        <div className="available-players">
                            {playerPool
                                .map(player => {
                                    const details = playerDetails?.find(p => p.id === player.id) || {};
                                    const positionType = selectedPosition.position.split('-')[0];
                                    const positionScore = calculatePositionScore(player, positionType);
                                    const isNaturalPosition = 
                                        (positionType === 'forward' && player.position_db === 'FWD') ||
                                        (positionType === 'midfield' && player.position_db === 'MID') ||
                                        (positionType === 'defense' && player.position_db === 'DEF');
                                    
                                    // Check if player is already assigned
                                    const isAssigned = whitePlayers.some(p => p.id === player.id) || 
                                                      colourPlayers.some(p => p.id === player.id);
                                    
                                    return {
                                        ...player,
                                        details,
                                        positionScore,
                                        isNaturalPosition,
                                        isAssigned
                                    };
                                })
                                .sort((a, b) => {
                                    // First sort by natural position
                                    if (a.isNaturalPosition && !b.isNaturalPosition) return -1;
                                    if (!a.isNaturalPosition && b.isNaturalPosition) return 1;
                                    // Then by position score
                                    return b.positionScore - a.positionScore;
                                })
                                .map(({ details, positionScore, isNaturalPosition, isAssigned }) => (
                                    <button 
                                        key={details.id}
                                        className={`player-option ${isNaturalPosition ? 'natural' : ''} ${isAssigned ? 'assigned' : ''}`}
                                        onClick={() => {
                                            assignPlayerToTeam(details, selectedPosition.team);
                                            setSelectedPosition(null);
                                        }}
                                        disabled={isAssigned}
                                    >
                                        <div className="player-info">
                                            <div className="player-name">
                                                {details.name}
                                                {details.recent_motm > 0 && <span className="status-icon">👑</span>}
                                                {details.skill_rating >= 2000 && <span className="status-icon">⭐</span>}
                                            </div>
                                            <div className="player-stats">
                                                <div className={`position ${isNaturalPosition ? 'natural' : ''}`}>
                                                    {details.position_db}
                                                </div>
                                                <div className="rating">{details.skill_rating}</div>
                                            </div>
                                            <div className="position-score">
                                                <div className="label">Position Fit</div>
                                                <div className={`value ${positionScore >= 80 ? 'high' : positionScore >= 60 ? 'medium' : 'low'}`}>
                                                    {positionScore}%
                                                </div>
                                            </div>
                                        </div>
                                        <div className="player-analysis">
                                            {details.player_analysis}
                                        </div>
                                    </button>
                                ))}
                        </div>
                    </div>
                </div>
            )}

            {playerPool.length != maxPlayers && whitePlayers.length === 0 && colourPlayers.length === 0 && (
                <div className="publish-button-disabled" style={{textAlign: 'center'}}>
                    Error: Not enough players in the pool to publish
                </div>
            )}

            {isAdmin && !isMatchFinished() && (
                <div className="team-actions">
                    <button 
                        className="save-button"
                        onClick={async () => {
                            try {
                                const matchId = getMatchId();
                                if (!matchId) {
                                    throw new Error('Cannot save: match_id is not available');
                                }
                                // Save formations
                                const results = await Promise.all([
                                    saveFormation(matchId, 'white', whiteFormation),
                                    saveFormation(matchId, 'colour', colourFormation),
                                    savePlayerPositions(matchId, [...whitePlayers, ...colourPlayers])
                                ]);
                                
                                alert('Teams saved successfully!');
                            } catch (error) {
                                console.error('Error saving teams:', error);
                                alert(`Failed to save teams: ${error.message}`);
                            }
                        }}
                    >
                        Save Draft
                    </button>
                    
                    <button 
                        className={`publish-button ${(needsTwoTeams ? (whitePlayers.length === 0 || colourPlayers.length === 0) : whitePlayers.length === 0) ? 'disabled' : ''}`}
                        disabled={(needsTwoTeams ? (whitePlayers.length === 0 || colourPlayers.length === 0) : whitePlayers.length === 0)}
                        title={(needsTwoTeams ? (whitePlayers.length === 0 || colourPlayers.length === 0) : whitePlayers.length === 0) ? 'Add players to teams before publishing' : ''}
                        onClick={async () => {
                            // Show warning confirmation
                            const totalPlayers = needsTwoTeams 
                                ? [...whitePlayers, ...colourPlayers].length
                                : whitePlayers.length;
                            const confirmMessage = `This will send SMS notifications to ${totalPlayers} players.\n\nAre you sure you want to publish the teams?`;
                            
                            if (!window.confirm(confirmMessage)) {
                                return;
                            }
                            
                            try {
                                const matchId = getMatchId();
                                // First save everything
                                const savePromises = [
                                    saveFormation(matchId, 'white', whiteFormation),
                                    savePlayerPositions(matchId, [...whitePlayers, ...colourPlayers])
                                ];
                                
                                // Only save colour formation for regular teams
                                if (needsTwoTeams) {
                                    savePromises.push(saveFormation(matchId, 'colour', colourFormation));
                                }
                                
                                await Promise.all(savePromises);
                                
                                // Then send SMS notifications with appropriate data
                                const requestData = {
                                    match_id: matchId,
                                    white_players: whitePlayers.map(player => ({
                                        player_id: parseInt(player.player_id || player.id),
                                        team_color: 'white'
                                    }))
                                };
                                
                                // Only include colour players for regular teams
                                if (needsTwoTeams) {
                                    requestData.colour_players = colourPlayers.map(player => ({
                                        player_id: parseInt(player.player_id || player.id),
                                        team_color: 'colour'
                                    }));
                                }
                                
                                const response = await api.post(`${process.env.REACT_APP_API_URL}/publish_teams.php`, requestData);

                                if (response.data.error) {
                                    throw new Error(`Failed to publish teams: ${response.data.error}`);
                                }
                                
                                setTeamsPublished(true); // Set teams as published after successful API call
                                alert('Teams published and notifications sent!');
                            } catch (error) {
                                console.error('Error publishing teams:', error);
                                alert(`Failed to publish teams: ${error.message}`);
                            }
                        }}
                    >
                        Publish Teams
                    </button>
                </div>
            )}
        

            {/* Always show the Create Social Post button, but disable it if teams haven't been published */}
            <button 
                className={`view-lineup-button ${!teamsPublished || (needsTwoTeams ? 
                    (whitePlayers.length === 0 || colourPlayers.length === 0) : 
                    (whitePlayers.length === 0)) ? 'disabled' : ''}`}
                disabled={!teamsPublished || (needsTwoTeams ? 
                    (whitePlayers.length === 0 || colourPlayers.length === 0) : 
                    (whitePlayers.length === 0))}
                onClick={() => setShowLineupList(true)}
                title={!teamsPublished ? 'Teams must be published first' : ''}
            >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
                    <path d="M12 15V3M7 10l5 5 5-5M4 17h16M4 21h16" />
                </svg>
                Create Social Post
            </button>
            
            {/* Add debug button for Super Admin users only */}
            {authService.isSuperAdmin() && (
            <button 
                className="debug-button"
                onClick={toggleDebugPanel}
                title="Show team formation debug information"
            >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
                    <path d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10zm-1-7v2h2v-2h-2zm0-8v6h2V7h-2z"/>
                </svg>
                Debug Analysis
            </button>
            )}

            {renderScreenshotPreview()}

            {showLineupList && (
                <div className="modal-overlay">
                    <div className="modal-content lineup-modal">
                        <button 
                            className="modal-close" 
                            onClick={() => setShowLineupList(false)}
                        >
                            ×
                        </button>
                        <LineupListView
                            teams={{
                                white: whitePlayers,
                                colour: needsTwoTeams ? colourPlayers : []
                            }}
                            teamNames={{
                                white: teamWhiteName,
                                colour: teamColourName
                            }}
                            match={match}
                            singleTeamMode={!needsTwoTeams}
                            playerPool={playerPool}
                        />
                    </div>
                </div>
            )}

            {isSorting && <SortingAnimation />}
            {renderPositionWarning()}
            
            {showDebugPanel && authService.isSuperAdmin() && <DebugPanel />}
        </div>
    );
};

// Add CSS styles for the debug panel
const style = document.createElement('style');
style.innerHTML = `
    /* Debug Panel Styles */
    .debug-button {
        background-color: #666;
        color: white;
        border: none;
        padding: 8px 15px;
        margin-left: 10px;
        border-radius: 4px;
        display: inline-flex;
        align-items: center;
        gap: 5px;
        cursor: pointer;
        font-size: 14px;
    }
    
    .debug-button svg {
        fill: currentColor;
        width: 20px;
        height: 20px;
    }
    
    .debug-button:hover {
        background-color: #777;
    }
    
    .debug-panel {
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        background-color: rgba(250, 250, 250, 0.98);
        border-radius: 8px;
        box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
        padding: 20px;
        width: 90%;
        max-width: 800px;
        max-height: 90vh;
        overflow-y: auto;
        z-index: 1000;
    }
    
    .debug-panel h3 {
        margin-top: 0;
        border-bottom: 1px solid #ddd;
        padding-bottom: 10px;
        color: #333;
    }
    
    .debug-section {
        margin-bottom: 20px;
        padding: 15px;
        background-color: #f5f5f5;
        border-radius: 6px;
    }
    
    .debug-section h4 {
        margin-top: 0;
        color: #444;
        font-size: 16px;
    }
    
    .debug-section ul {
        margin: 0;
        padding-left: 20px;
    }
    
    .debug-section li {
        margin-bottom: 5px;
    }
    
    .close-debug-btn {
        position: absolute;
        top: 15px;
        right: 15px;
        background-color: #f44336;
        color: white;
        border: none;
        padding: 5px 10px;
        border-radius: 4px;
        cursor: pointer;
    }
    
    .debug-alert {
        border-left: 4px solid #f44336;
        background-color: #ffebee;
    }
    
    .debug-recommendations {
        margin-top: 15px;
        padding: 10px;
        background-color: rgba(255, 255, 255, 0.5);
        border-radius: 4px;
    }
    
    .debug-recommendations h5 {
        margin-top: 0;
        color: #333;
    }

    .formation-summary .formation-effectiveness {
        display: flex;
        flex-direction: column;
        flex: 1;
        padding: 12px;
        background: #242424;
        border-radius: 8px;
        position: relative;
    }

    /* Debug styles for formations list */
    .formation-debug {
        background: rgba(0, 0, 0, 0.2);
        border-radius: 4px;
        padding: 8px;
        margin-top: 10px;
        font-size: 12px;
        border: 1px dashed #666;
    }

    .formation-debug .debug-title {
        font-weight: bold;
        margin-bottom: 5px;
        color: #ff9800;
    }

    .formation-debug .available-formations {
        display: flex;
        flex-wrap: wrap;
        gap: 5px;
        margin-bottom: 5px;
    }

    .formation-debug .formation-item {
        padding: 2px 6px;
        background: #333;
        border-radius: 4px;
    }

    .formation-debug .formation-item.active {
        background: #007bff;
        color: white;
    }

    .formation-debug .debug-info {
        margin-top: 5px;
        color: #aaa;
        display: flex;
        flex-wrap: wrap;
        gap: 10px;
    }

    .formation-summary .formation-stats {
        display: flex;
        justify-content: space-between;
        margin-bottom: 10px;
    }

    .formation-summary .stat {
        flex: 1;
        text-align: center;
    }

    .formation-summary .label {
        font-weight: bold;
        display: block;
        margin-bottom: 5px;
    }

    .formation-summary .stat-bar {
        height: 10px;
        background-color: #ddd;
        border-radius: 4px;
        overflow: hidden;
    }

    .formation-summary .stat-fill {
        height: 100%;
        display: block;
    }

    .formation-summary .recommended .stat-fill {
        background-color: #4caf50;
    }

    .formation-summary .current .stat-fill {
        background-color: #ff9800;
    }

    .formation-summary .stat-value {
        display: block;
        margin-top: 5px;
        font-weight: bold;
    }

    .formation-debug .formation-item.recommended {
        background: #007bff;
        color: white;
    }

    .formation-debug .formation-item.alternate {
        background: #555;
        color: #ddd;
        cursor: pointer;
    }

    .formation-debug .formation-item.alternate:hover {
        background: #666;
        color: white;
    }

    .formation-debug .keeper-toggle {
        margin-top: 10px;
        padding: 5px 10px;
        border: none;
        border-radius: 4px;
        cursor: pointer;
        font-weight: bold;
    }

    .formation-debug .keeper-toggle.enabled {
        background-color: #dc3545;
        color: white;
    }

    .formation-debug .keeper-toggle.disabled {
        background-color: #28a745;
        color: white;
    }
`;
document.head.appendChild(style);

/**
 * Team Builder Scoring Algorithm Debug
 * ====================================
 * 
 * This section provides detailed information about how the team building algorithm works,
 * particularly useful for coaches and managers to understand team selection decisions.
 * 
 * To use this debug information:
 * 1. Open browser console (F12 or right-click > Inspect > Console)
 * 2. Click "Sort Players" button
 * 3. Review the detailed breakdown of player assignments and scoring
 * 
 * Example Scenario Analysis:
 * --------------------------
 * When "Sort Players" is clicked, the following process occurs:
 * 
 * 1. Player Sorting:
 *    - Players are sorted by form rating (higher rated first)
 *    - Position is used as a secondary sort criterion
 *    - Example: [Player A (FWD, form 85), Player B (MID, form 85), Player C (DEF, form 80)]
 * 
 * 2. Team Distribution:
 *    - Players are distributed alternately between teams
 *    - White team gets even indices (0, 2, 4...)
 *    - Color team gets odd indices (1, 3, 5...)
 *    - Example: White: [Player A, Player C], Color: [Player B]
 * 
 * 3. Formation Selection:
 *    - Best formation is calculated for each team using findBestFormation()
 *    - Each formation is evaluated based on player attributes and positions
 *    - Example: White team optimal formation: 1-1-1, Color team: 0-1-0
 * 
 * 4. Position Assignment:
 *    - Players are assigned to positions using assignPlayersToFormation()
 *    - Forward positions are filled first (prioritizing shooting/pace)
 *    - Then defensive positions (prioritizing defending/physical)
 *    - Finally midfield positions (prioritizing passing/dribbling)
 *    - Example: Player A → forward-center, Player C → defense-center
 * 
 * Position Scoring Formula:
 * -------------------------
 * Position score = (Weighted attribute score) × (Position suitability multiplier)
 * 
 * Weighted attribute score calculation:
 * - Forward: (0.35×shooting + 0.25×pace + 0.20×dribbling + 0.15×passing + 0.05×physical) / 100
 * - Midfield: (0.30×passing + 0.25×dribbling + 0.15×shooting + 0.15×defending + 0.10×pace + 0.05×physical) / 100
 * - Defense: (0.35×defending + 0.25×physical + 0.20×pace + 0.15×passing + 0.05×dribbling) / 100
 * 
 * Position suitability multipliers:
 * - Natural position: 1.0-1.2× (e.g., FWD in forward position)
 * - Adjacent position: 0.7-0.9× (e.g., MID in forward position)
 * - Non-natural position: 0.3-0.6× (e.g., DEF in forward position)
 * 
 * Recent Form Consideration:
 * -------------------------
 * - Players with higher form ratings are prioritized in team selection
 * - Form rating directly affects the initial sorting of players
 * - This ensures in-form players are selected first and placed in optimal positions
 * 
 * Example Player Scoring:
 * ----------------------
 * Player: John (FWD)
 * Attributes: Shooting 85, Pace 80, Dribbling 75, Passing 65, Defending 40, Physical 70
 * 
 * Score for forward position:
 * = ((85×0.35 + 80×0.25 + 75×0.20 + 65×0.15 + 70×0.05) / 100) × 1.2
 * = (29.75 + 20 + 15 + 9.75 + 3.5) / 100 × 1.2
 * = 0.78 × 1.2
 * = 93.6 (rounded to 94)
 * 
 * Score for midfield position:
 * = ((65×0.30 + 75×0.25 + 85×0.15 + 40×0.15 + 80×0.10 + 70×0.05) / 100) × 0.9
 * = (19.5 + 18.75 + 12.75 + 6 + 8 + 3.5) / 100 × 0.9
 * = 0.685 × 0.9
 * = 61.65 (rounded to 62)
 * 
 * Score for defense position:
 * = ((40×0.35 + 70×0.25 + 80×0.20 + 65×0.15 + 75×0.05) / 100) × 0.6
 * = (14 + 17.5 + 16 + 9.75 + 3.75) / 100 × 0.6
 * = 0.61 × 0.6
 * = 36.6 (rounded to 37)
 * 
 * This player would be best suited for a forward position (score: 94).
 * 
 * To enable detailed console logging of the team building process:
 * 1. Open browser console (F12 or right-click > Inspect > Console)
 * 2. Click "Sort Players" button
 * 3. Check browser console for detailed breakdown
 */

export default TeamBuilder;